/* eslint-disable import/no-cycle */
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import {
  differenceWith,
  intersectionWith,
  isEqual,
  map,
  orderBy,
  uniqBy,
  groupBy,
  get,
} from "lodash";
import { Semaphore } from "await-semaphore";

import moment from "moment";
import {
  changeCategory,
  connectTag,
  createClientFromPhone,
  getPlatformApiFromPhone,
  deleteConversationCallback,
  disconnectTag,
  getClientInfo,
  getConversations,
  sendToCrm,
  updateClientInfo,
  updateClientUsernameMutation,
  updateValuableByUserMutation,
  updateClientPhoneMutation,
  checkExistedPhone,
  deleteConversation,
  setIsArchived,
  getClientReport,
  getBroadCastConversationsQuery,
  getClientReportToExport,
  getConversationID,
  getConversationForRealtime,
} from "../utils/getConversations";
import {
  applyVisibilityFlags,
  isConversationVisibilityExempt,
} from "../utils/conversationVisibility";
import { isConversationScopeRestricted } from "../utils/conversationScope";
import { assignAgent } from "../utils/getAgents";
import { getLastSurveyInfo } from "../utils/survey";
import {
  ADD_NEW_MESSAGES,
  clearMessagesAction,
  handleNewMessagesButtonAction,
  isLocalSendFailure,
  updateMessageStatusId,
  seenMessage,
  setWindowLastClientMessageAction,
  setWindowRemainingFromStatusEventAction,
  getMessagesAction
} from "./messagesDuck";
import { setAppStatus } from "./appDuck";
import { setActualSpaceAction } from "./spacesDuck";
import CONSTANTS from "../const/constants";
import { isDialogWindowOpen } from "../tools/date-utils";
import cleanString from "../utils/cleanString";
import client from "../apollo/apolloClient";
import ApolloAgents from "../apollo/agents";
import { getAllAgents } from "./agentsDuck";
import { getClientInformationUtil } from "../utils/datasheet";
import ApolloConversations from "../apollo/conversations";
import API from "../axios";
import { getPhoneOrNull, isUserIdIdentity } from "../utils/whatsappIdentity";
import {
  TELEMETRY_SOURCES,
  normalizeSeal,
  withTelemetrySource,
} from "../utils/conversationTelemetry";
import {
  shouldIgnoreRealtimeConversation,
  isRealtimeMessageAlreadyCounted,
} from "../utils/conversationLimit/realtimeFilter";

/**
 * The provenance seal a conversation-carrying Pusher event brings at its top
 * level (door 'pusher'), normalized to its seven fields, or null. Only for
 * telemetry: nothing in the dashboard decides anything on it.
 */
const pusherSealOf = (data) => normalizeSeal(data && data.prov);

// Motivos con los que los thunks de este duck reportan un fallo. Se devuelven en
// una forma DISCRIMINADA ({ error: true, reason }) en vez del Error crudo: un
// Error de JS no tiene `.error`, así que devolverlo hacía que el consumidor lo
// leyera como éxito. Ver #216.
export const DUPLICATED_PHONE = "DUPLICATED_PHONE";
// Este camino ya avisa por su cuenta con un mensaje específico; el consumidor NO
// debe pintar otro toast encima o el usuario ve dos. Ver #216.
export const PHONE_NOT_EDITABLE = "PHONE_NOT_EDITABLE";
export const UNEXPECTED_ERROR = "UNEXPECTED_ERROR";

const semaphoreDeleteConversation = new Semaphore(1);

const CONVERSATIONS_INITIAL_STATE = {
  fetching: false,
  fetched: false,
  data: [],
  current: {},
  isCurrentWindowOpen: false,
  isFirstQuery: true,
  searchFilter: "",
  searchOrigin: "",
  isClientInfoOpen: false,
  handleScroll: false,
  source: null, // request, subscription
  hasMore: true,
  unreadBroadcastChat: 0,
  isNewBroadcastChat: true,
  allUsersForFilter: [], // used for filter in Vinte admins
};

let onSnapshot = null;
let subscriptionObserver = null;
let subscriptionObserverMessageStatus = null;
let unsassignedAgentObserver = null;

// constants
export const SET_CONVERSATIONS = "SET_CONVERSATIONS";
const GET_CONVERSATIONS = "GET_CONVERSATIONS";
export const GET_CONVERSATIONS_SUCCESS = "GET_CONVERSATIONS_SUCCESS";
const GET_CONVERSATIONS_ERROR = "GET_CONVERSATIONS_ERROR";
export const SET_CONVERSATION_AS_SELECTED = "SET_CONVERSATION_AS_SELECTED";
const CLEAR_CONVERSATIONS = "CLEAR_CONVERSATIONS";
const CLEAR_SELECTED_CONVERSATION = "CLEAR_SELECTED_CONVERSATION";
const UPDATE_CLIENT_INFO = "UPDATE_CLIENT_INFO";
const SET_SEARCH_FILTER = "SET_SEARCH_FILTER";
const SET_CURRENT_WINDOW_OPEN = "SET_CURRENT_WINDOW_OPEN";
const HANDLE_CLIENT_INFO = "HANDLE_CLIENT_INFO";
const HANDLE_SCROLL = "HANDLE_SCROLL";
const THERE_IS_NO_MORE_CONVERSATIONS = "THERE_IS_NO_MORE_CONVERSATIONS";
const THERE_IS_MORE_CONVERSATIONS = "THERE_IS_MORE_CONVERSATIONS";
const UPDATE_REASSING_TAG = "UPDATE_REASSING_TAG";
const UPDATE_USER_NAME = "UPDATE_USER_NAME";
const UPDATE_USER_PHONE = "UPDATE_USER_PHONE";
const CLEAR_DATA = "CLEAR_DATA";
const UPDATE_VALUABLE_CLASSIFICATION = "UPDATE_VALUABLE_CLASSIFICATION";
const UPDATE_HASFOLLOWUPMESSAGE = "UPDATE_HASFOLLOWUPMESSAGE";
const UPDATE_LAST_MESSAGE_CONVERSATION = "UPDATE_LAST_MESSAGE_CONVERSATION";
const RESET_CONVERSATION_CURRENT = "RESET_CONVERSATION_CURRENT";
const GET_BROADCAST_CONVERSATIONS = "GET_BROADCAST_CONVERSATIONS";
const UPDATE_UNREAD_BROADCAST_CHAT = "UPDATE_UNREAD_BROADCAST_CHAT";
const SET_CURRENT_CONVERSATION = "SET_CURRENT_CONVERSATION";
const SET_USER_FILTERS = "SET_USER_FILTERS";
const SET_CURRENT_LAST_SURVEY_INFO = "SET_CURRENT_LAST_SURVEY_INFO";
const SET_CONVERSATION_CLOSED = "SET_CONVERSATION_CLOSED";
const SET_LAST_SURVEY_INFO = "SET_LAST_SURVEY_INFO";
const UPDATE_CONVERSATION_VISIBILITY = "UPDATE_CONVERSATION_VISIBILITY";

// reducer
export default function conversationsReducer(
  state = CONVERSATIONS_INITIAL_STATE,
  action
) {
  const { type, payload } = action;
  switch (type) {
    case SET_CONVERSATIONS:
      return { ...state, data: payload };
    case GET_CONVERSATIONS:
      if (payload) return { ...state, fetching: true, current: {} };
      return { ...state, fetching: true };
    case GET_CONVERSATIONS_SUCCESS:
      return {
        ...state,
        fetching: false,
        fetched: true,
        data: payload.conversations,
        source: payload.source,
        isFirstQuery: false,
      };
    case GET_CONVERSATIONS_ERROR:
      return { ...state, fetching: false, error: payload, current: {} };
    case SET_CURRENT_WINDOW_OPEN:
      return { ...state, isCurrentWindowOpen: true };
    case SET_CONVERSATION_AS_SELECTED:
      return {
        ...state,
        data: payload.data,
        current: {
          ...payload.current,
          tags:
            payload?.current?.tags?.length > 0
              ? uniqBy(payload.current.tags, "id")
              : [],
        },
        isCurrentWindowOpen: payload.isCurrentWindowOpen,
        source: payload.source || state.source,
      };
    case SET_LAST_SURVEY_INFO:
      return {
        ...state,
        current: {
          ...state.current,
          lastSurveyInfo: payload,
        },
      };
    case CLEAR_SELECTED_CONVERSATION:
      return { ...state, current: {} };
    case SET_SEARCH_FILTER:
      return {
        ...state,
        searchFilter: payload?.keyword,
        searchOrigin: payload?.searchOrigin,
      };
    case HANDLE_CLIENT_INFO:
      return { ...state, isClientInfoOpen: payload };
    case HANDLE_SCROLL:
      return { ...state, handleScroll: !state.handleScroll };
    case UPDATE_CLIENT_INFO:
      return {
        ...state,
        current: {
          ...state.current,
          ...payload,
          tags:
            state?.current?.tags?.length > 0
              ? uniqBy(state.current.tags, "id")
              : [],
        },
      };

    case SET_CURRENT_LAST_SURVEY_INFO:
      return {
        ...state,
        current: {
          ...state.current,
          lastSurveyInfo: { createdAt: new Date() },
          isClosed: true,
        },
      };
    case SET_CURRENT_LAST_SURVEY_INFO:
      return {
        ...state,
      };
    case THERE_IS_NO_MORE_CONVERSATIONS:
      return { ...state, hasMore: false };
    case THERE_IS_MORE_CONVERSATIONS:
      return { ...state, hasMore: true };
    case UPDATE_USER_NAME:
      return {
        ...state,
        current: {
          ...state.current,
          nameFromChannel: payload.nameFromChannel,
          cleanedName: payload.cleanedName,
        },
      };
    case GET_BROADCAST_CONVERSATIONS:
      return {
        ...state,
        unreadBroadcastChat: payload.unreadBroadcastChat,
      };
    case UPDATE_UNREAD_BROADCAST_CHAT:
      return {
        ...state,
        isNewBroadcastChat: payload.isNewBroadcastChat,
      };
    case UPDATE_USER_PHONE:
      return {
        ...state,
        current: {
          ...state.current,
          cleanedName: payload.cleanedName,
          key: payload.key,
          senderId: payload.senderId,
        },
      };
    case UPDATE_VALUABLE_CLASSIFICATION:
      return {
        ...state,
        current: {
          ...state.current,
          setValuableByUser: payload.value,
          isConversationValuable: payload.value,
        },
        data: payload.data,
      };
    case UPDATE_REASSING_TAG:
      return {
        ...state,
        current: {
          ...state.current,
          tags: payload?.length > 0 ? uniqBy(payload, "id") : [],
        },
      };
    case CLEAR_CONVERSATIONS:
      return { ...CONVERSATIONS_INITIAL_STATE };
    case CLEAR_DATA:
      return { ...CONVERSATIONS_INITIAL_STATE };
    case UPDATE_HASFOLLOWUPMESSAGE:
      return {
        ...state,
        current: {
          ...state.current,
          hasFollowUpMessage: payload.hasFollowUpMessage,
          tags:
            state?.current?.tags?.length > 0
              ? uniqBy(state.current.tags, "id")
              : [],
        },
        data: payload.data,
      };
    case UPDATE_LAST_MESSAGE_CONVERSATION:
      return { ...state, payload };
    case RESET_CONVERSATION_CURRENT:
      return { ...state, current: {} };
    case SET_CURRENT_CONVERSATION:
      return { ...state, current: payload };
    case SET_USER_FILTERS:
      return { ...state, allUsersForFilter: payload };
    case UPDATE_CONVERSATION_VISIBILITY:
      return {
        ...state,
        ...applyVisibilityFlags(
          { data: state.data, current: state.current },
          payload.clientId,
          payload.flags
        ),
      };
    default:
      return state;
  }
}

// actions
export const deleteOnSnapshotConversationsListenerAction = () => () => {
  if (subscriptionObserver) {
    subscriptionObserver.unsubscribe();
    subscriptionObserver = null;
  }
  if (subscriptionObserverMessageStatus) {
    subscriptionObserverMessageStatus.unsubscribe();
    subscriptionObserverMessageStatus = null;
  }
  if (unsassignedAgentObserver) {
    unsassignedAgentObserver.unsubscribe();
    unsassignedAgentObserver = null;
  }
  if (onSnapshot) {
    onSnapshot();
    onSnapshot = null;
  }
};

export const unassignedAgentObservableNext = async (
  value,
  getState,
  dispatch,
  subscriptionFilters
) => {
  const release = await semaphoreDeleteConversation.acquire();
  try {
    if (value?.data?.client?.node) {
      const { newConversations, newUnsubscribeQuery } =
        await deleteConversationCallback(
          value?.data?.client?.node,
          getState().conversations.data,
          subscriptionFilters
        );
      if (newConversations) {
        const currentConversationId = getState().conversations.current?.id;
        if (currentConversationId === value?.data?.client?.node.id) {
          const payload = { data: newConversations, current: {} };
          dispatch(
            withTelemetrySource(
              {
                type: SET_CONVERSATION_AS_SELECTED,
                payload: {
                  ...payload,
                  source: CONSTANTS.CONVERSATIONS_SOURCE_SUBSCRIPTION,
                },
              },
              TELEMETRY_SOURCES.SUBSCRIPTION
            )
          );
        } else {
          // Labelled `subscription`: untagged, GET_CONVERSATIONS_SUCCESS reads
          // as `query`, and a `query` write resets the telemetry's list
          // baseline to the active space — which this partial rewrite of the
          // list already on screen does not establish.
          dispatch(
            withTelemetrySource(
              {
                type: GET_CONVERSATIONS_SUCCESS,
                payload: {
                  conversations: newConversations,
                  source: CONSTANTS.CONVERSATIONS_SOURCE_SUBSCRIPTION,
                },
              },
              TELEMETRY_SOURCES.SUBSCRIPTION
            )
          );
        }
      }

      if (newUnsubscribeQuery) {
        if (unsassignedAgentObserver) {
          unsassignedAgentObserver.unsubscribe();
          unsassignedAgentObserver = null;
        }
        unsassignedAgentObserver = newUnsubscribeQuery.subscribe({
          next: (value) =>
            unassignedAgentObservableNext(
              value,
              getState,
              dispatch,
              subscriptionFilters
            ),
          error: (error) => console.log(error),
        });
      }
    }
    release();
  } catch (error) {
    console.log(error);
    release();
  }
};

export const updateLastMessage =
  (
    conversationId,
    messageId,
    messageStatus,
    errorCode = null,
    errorInfo = null,
    windowExpiration
  ) =>
  (dispatch, getState) => {
    const conversations = [...getState().conversations.data];
    const currentId = getState().conversations?.current?.id;

    const newConversations = conversations.map((c) => {
      if (c.id === conversationId && c?.lastMessage?.id === messageId) {
        // The server reports a status for a message this client had marked as
        // failed (`updateMessageWithError`): the message did reach it, so the
        // local mark gives way to that status in both branches below.
        const lastMessage = isLocalSendFailure(c.lastMessage)
          ? { ...c.lastMessage, sent: true, error: false, messageStatus }
          : c.lastMessage;
        if (
          (messageStatus === "sent" || messageStatus === "delivered") &&
          windowExpiration
        ) {
          const currentTimestamp = new Date().getTime();

          const diff = moment.duration(
            windowExpiration * 1000 - currentTimestamp,
            "milliseconds"
          );
          const diffMinutes = Math.floor(diff.asMinutes());

          // The banner of the open conversation does not take this value as
          // it comes: the event's expiration (epoch seconds) is often the
          // send time of the message, so it goes through the same rule as the
          // window read and cannot close a window the customer opened.
          if (currentId === conversationId) {
            dispatch(
              setWindowRemainingFromStatusEventAction(
                conversationId,
                windowExpiration * 1000
              )
            );
          }

          // Raw value of the event, kept as it was: nothing reads it.
          return {
            ...c,
            lastMessage,
            windowRemainingTime: diffMinutes,
          };
        } else {
          return {
            ...c,
            lastMessage: {
              ...lastMessage,
              messageStatus,
              errorCode,
              errorInfo,
            },
          };
        }
      }
      return { ...c };
    });

    dispatch({ type: SET_CONVERSATIONS, payload: newConversations });
  };

/**
 * Where a conversation stands against the selected space, by its channel.
 * The chat works inside one space (its inbox, its templates), so the open
 * conversation has to be one of that space's.
 *
 * `inSelectedSpace` is also true when it cannot be told: a conversation
 * without channel data, a selected space without a channel list, or a user
 * with a single space (whose inbox is not always built from that list). Those
 * cases keep working as they did before this check existed.
 * `otherSpace` is the user's space that has the channel when it is not the
 * selected one, or `null` when none of them has it.
 */
const locateConversationSpace = (conversation, spacesState) => {
  const selectedSpace = spacesState?.selected;
  const userSpaces = Array.isArray(spacesState?.data) ? spacesState.data : [];
  const channelId = conversation?.channel?.id;
  const hasChannel = (space) =>
    Array.isArray(space?.channels) &&
    space.channels.some((channel) => channel?.id === channelId);

  if (
    !channelId ||
    userSpaces.length < 2 ||
    !Array.isArray(selectedSpace?.channels) ||
    hasChannel(selectedSpace)
  ) {
    return { inSelectedSpace: true, otherSpace: null };
  }
  return {
    inSelectedSpace: false,
    otherSpace:
      userSpaces.find(
        (space) => space?.id !== selectedSpace.id && hasChannel(space)
      ) || null,
  };
};

/**
 * Trail of the space guards in Sentry: neither a dropped load nor a deep link
 * that selects another space dispatches an action of its own to tell them
 * apart. No ids, no conversation data.
 */
const addSpaceGuardBreadcrumb = (message, data) => {
  try {
    Sentry.addBreadcrumb({ category: "chat.space", level: "info", message, data });
  } catch (e) {
    // Telemetry must never break the inbox load.
  }
};

// Sequence of the restart loads, the ones that replace the whole inbox and
// settle `fetching`: a load can tell whether a newer one started after it did
// (see `leaveToSelectedSpace` below).
let latestRestartLoadId = 0;
// The restart load that `leaveToSelectedSpace` started last, if any.
let recoveryLoadId = 0;

export const getConversationsAction =
  (restart, limit, chatId=null, skipOverride=null) => async (dispatch, getState) => {
    // La conversación abierta, leída ANTES de que `restart` la borre del
    // store (`GET_CONVERSATIONS` con payload `true` deja `current: {}`).
    // Es la fuente preferida para reponerla al final: ya la tenemos entera
    // en memoria, así que reponerla no depende de que la red responda.
    //
    // Se declara FUERA del `try` a propósito: el `catch` también la necesita.
    // Cuando falla la consulta de la BANDEJA —una consulta distinta a la de la
    // conversación—, el `catch` despacha `GET_CONVERSATIONS_ERROR` y el reducer
    // vuelve a dejar `current: {}`, así que el chat se cerraba igual. Era la
    // segunda causa que este PR declaraba como deuda; se cierra aquí.
    const openedConversation = getState().conversations.current;

    // Space guards. Also read before anything is dispatched: the space this
    // load is for, and whether it is the first attempt to load the chat. No
    // load has finished yet when neither `fetched` (set by a success) nor
    // `error` (set by a failure) is there; only clearing the conversations
    // removes them, so a failed first load counts as an attempt too.
    const requestedSpaceId = getState().spaces?.selected?.id;
    const { fetched, error: previousLoadError } = getState().conversations;
    const isFirstLoad = !fetched && !previousLoadError;
    if (restart) latestRestartLoadId += 1;
    const restartLoadId = latestRestartLoadId;
    const isRecoveryLoad = restart && restartLoadId === recoveryLoadId;
    const spaceChanged = () =>
      getState().spaces?.selected?.id !== requestedSpaceId;
    const supersededByRestart = () => restartLoadId !== latestRestartLoadId;
    // When the selected space changed while this load was in flight, its
    // result (or its failure) belongs to the previous space and is dropped:
    // applying it would put that space's inbox, and its open conversation,
    // back on screen. If no restart load started meanwhile, nothing else
    // would settle `fetching` (the whole chat area is a loader while it is
    // true), so the selected space's inbox is loaded instead.
    const leaveToSelectedSpace = () => {
      const reloads = !supersededByRestart();
      addSpaceGuardBreadcrumb("inbox load dropped: the selected space changed", {
        reloads,
      });
      if (!reloads) return undefined;
      recoveryLoadId = latestRestartLoadId + 1;
      return dispatch(getConversationsAction(true, limit));
    };
    // That reload gives way to a restart load that started after it. The
    // space switcher selects the space and only then starts its own reload:
    // a previous-space load that settles in between reloads first, and two
    // reloads of the same space would both reach the store. The newer one is
    // the one applied, and it settles `fetching`.
    const gaveWayToNewerRestart = () => isRecoveryLoad && supersededByRestart();

    try {
      dispatch(deleteOnSnapshotConversationsListenerAction());
      if (restart) {
        dispatch(clearMessagesAction());
        dispatch({ type: HANDLE_CLIENT_INFO, payload: false });
        dispatch({ type: HANDLE_SCROLL, payload: null });
        dispatch({ type: GET_CONVERSATIONS, payload: restart });
      }
      const selectedSpaces = getState().spaces.selected;
      const selectedFilterType = getState().filters.type;
      const rolName = getState().user.data.rol.name;

      const { channels } = selectedSpaces;

      const actualAgentId = getState().user.data.id;

      let selectedFilters = [];
      selectedFilters = getState().filters.selected;
      let userRol1 = "";
      userRol1 = getState().user.data.rol.name.toLowerCase();


      if (selectedSpaces?.id === "650dcadea7b11b000891b536") {
        selectedFilters.agents = [
          {
            id: CONSTANTS.UNASSIGNED_AGENT_ID,
            name: "Sin asignar",
            selected: false,
          },
        ];
      } else {
        if (selectedFilterType === CONSTANTS.FILTER_ASSIGNED) {
          selectedFilters.agents = [{ id: actualAgentId }];
        } else if (selectedFilterType === CONSTANTS.FILTER_UNREADMESSAGES) {
          selectedFilters.unreadMessages = true;
        } else if (selectedFilterType === CONSTANTS.FILTER_UNANSWERED) {
          selectedFilters.unanswered = true;
        }
        // A conversation-restricted admin (Inicio, spec S12) sees the list
        // exactly like an agent/vendedor: only what is assigned to them.
        if (
          userRol1 === CONSTANTS.AGENT ||
          userRol1 === "vendedor" ||
          isConversationScopeRestricted(getState().user.data)
        ) {
          selectedFilters.agents = [{ id: actualAgentId }];
        }
      }

      // Apply quick filters from filters state (unreadMessages, unanswered toggles)
      if (getState().filters.unreadMessages) {
        selectedFilters.unreadMessages = true;
      }
      if (getState().filters.unanswered) {
        selectedFilters.unanswered = true;
      }

      if (getState()?.conversations?.searchOrigin === "BOARDS") {
        selectedFilters.selectedChannelsIds = channels
          .filter((c) => c?.platform?.name !== CONSTANTS.SMS_PLATFORM)
          .map((e) => e.id);
      }

      const searchValue = getState().conversations.searchFilter;
      const conversations = getState().conversations.data;
      const conversationsFilter = getState().conversations.searchFilter;

      const companyId = getState().user.data.company.id;
      const userId = getState().user.data.id;
      const userSpaces = getState().user.data.spaces;
      const isAdminOfIds = getState().user.data.isAdminOfIds;
      selectedFilters.companyId = companyId;

      let selectedPagesIds = [];

      if (
        userSpaces.length === 1 &&
        userSpaces[0]?.name === CONSTANTS.VINTE_DEFAULT_NAME
      ) {
        selectedPagesIds = [
          CONSTANTS.VINTE_DEFAULT_WHATSAPP,
          CONSTANTS.VINTE_DEFAULT_FACEBOOK,
        ];
      } else {
        selectedPagesIds = channels
          .filter((c) => c?.platform?.name !== CONSTANTS.SMS_PLATFORM)
          .map((e) => e.id);
      }

      const selectedPagesIdsCleaned = selectedPagesIds.filter((e) => e);
      selectedFilters.selectedSpacesIds = selectedPagesIdsCleaned;

      // Rows the client pinned on top for the open conversation
      // (`sendNewMessageAction`, the Pusher insert) are not part of the
      // server's result set for these filters: counting them would make the
      // next page skip one real conversation.
      const fetchedCount = conversations.filter((c) => !c?.outsideResults).length;
      selectedFilters.skip = restart ? 0 : (skipOverride !== null ? skipOverride : fetchedCount);
      selectedFilters.first = limit || 20;

      if (searchValue) {
        selectedFilters.cleanedName = cleanString(searchValue);
      } else selectedFilters.cleanedName = "";

      const companyName = getState().user.data.company.name;
      selectedFilters.isVinte = companyName === CONSTANTS.VINTE_COMPANY_NAME;
      selectedFilters.isAgent =
        rolName !== CONSTANTS.SUPERADMIN &&
        rolName !== CONSTANTS.ADMIN &&
        rolName !== CONSTANTS.VINTE_COMMUNITY_MANAGER;

      if (rolName === CONSTANTS.GROUPADMIN) {
        selectedFilters.groupAdminId = userId;
        selectedFilters.channels = channels;
      }

      // const aiBotVisibleToBotAdminOnly =
      //   getState().user.data.aiBotConfig?.visibleToBotAdminOnly;
      // const aiBotBotAdminRoleIds =
      //   getState().user.data.aiBotConfig?.botAdminRoleIds;
      // console.log("aiBotVisibleToBotAdminOnly => ", aiBotVisibleToBotAdminOnly);
      // console.log("aiBotBotAdminRoleIds => ", aiBotBotAdminRoleIds);
      // console.log("userId => ", userId);
      // if (
      //   aiBotVisibleToBotAdminOnly &&
      //   !aiBotBotAdminRoleIds?.includes(userId)
      // ) {
      //   selectedFilters.aiBotVisibleToBotAdminOnly = true;
      // }

      let fetchedConversations = [];
      let cconversation = null;
      if (chatId) {
        if (openedConversation?.id === chatId) {
          // Camino normal de la recuperación tras una reconexión: el id que
          // llega es el de la conversación que el asesor ya tenía abierta, o
          // sea que la conversación entera ya está aquí. Volver a pedirla por
          // red sería preguntar por algo que ya sabemos, y encima en el peor
          // momento posible.
          cconversation = openedConversation;
        } else {
          try {
            // Ojo: esto puede resolver `null` sin lanzar, cuando la
            // conversación existió pero ya no está (borrada, o fuera del
            // alcance del usuario). Ese caso NO es un fallo y no se repone
            // nada: el chat queda cerrado a propósito, porque reponer una
            // conversación que el servidor dice que ya no existe sería
            // enseñarle al asesor algo que no puede usar. Se distingue del
            // fallo de red justamente porque no lanza.
            cconversation = await getConversationID(chatId);
          } catch (error) {
            // La consulta falló (enlace directo a una conversación que no es
            // la abierta, y la red no respondió). Se CONSERVA lo que hubiera
            // abierto en vez de dejar el chat cerrado: perder la bandeja un
            // momento es recuperable, quedarse sin el chat en el que estabas
            // escribiendo no lo es.
            console.error("ERROR getConversationID => ", error);
            // El evento duplicado que señaló Diego: si el fallo es de nivel
            // GraphQL (`response.errors[]`), `observability/apolloLink.js` YA
            // lo reporta por su cuenta, así que capturarlo otra vez aquí deja
            // dos eventos del mismo fallo en Sentry.
            //
            // Quitar la captura del todo tampoco vale: `apolloLink` declara
            // explícitamente que NO captura network errors, y el fallo de red
            // es justo el caso que importa aquí —la reconexión ocurre porque
            // la red está mal—. Quitarla dejaría ciego el único escenario que
            // este arreglo existe para cubrir.
            //
            // Así que se reporta sólo lo que la otra capa no reporta. Un
            // `ApolloError` de nivel GraphQL trae `graphQLErrors` con al menos
            // un elemento; el fallo de red no. Ese es el discriminante.
            const yaReportadoPorApolloLink =
              Array.isArray(error?.graphQLErrors) &&
              error.graphQLErrors.length > 0;
            if (!yaReportadoPorApolloLink) {
              Sentry.captureException(error, {
                tags: { component: "conversationsDuck", op: "getConversationID" },
              });
            }
            cconversation = openedConversation?.id ? openedConversation : null;
          }
        }
      }

      // The conversation to open has to be one of the selected space's (see
      // `locateConversationSpace`).
      if (cconversation?.id && !spaceChanged()) {
        const { inSelectedSpace, otherSpace } = locateConversationSpace(
          cconversation,
          getState().spaces
        );
        if (!inSelectedSpace) {
          // Deep link (`/chat/:chatId`) on the first attempt to load the
          // chat: the conversation was asked for by id and lives in another
          // space of the user, so that space is selected through the same
          // action the switcher uses and the load starts over there. Only on
          // that attempt, whether it then succeeds or fails: later reloads
          // reuse the id left in the URL (reconnect), and they must not move
          // the user out of the space chosen since then.
          const isDeepLink = cconversation !== openedConversation;
          if (isDeepLink && isFirstLoad && otherSpace) {
            await dispatch(setActualSpaceAction(otherSpace));
            if (getState().spaces?.selected?.id === otherSpace.id) {
              addSpaceGuardBreadcrumb("deep link selected the conversation's space");
              await dispatch(
                getConversationsAction(restart, limit, chatId, skipOverride)
              );
              return;
            }
          }
          // Anything else is not opened: a conversation kept in memory from
          // another space, or one whose channel no space of the user has.
          // Same outcome as a conversation the server no longer returns.
          cconversation = null;
        }
      }

      if (companyName === CONSTANTS.VELAS_COMPANY_NAME) {
        const user = getState().user.data;
        const roleName = user.rol.name;
        if (roleName === CONSTANTS.ADMIN) {
          const channels = selectedSpaces.channels.map((channel) => channel.id);
          selectedFilters.selectedChannelsIds = channels;
        }
      }

      if (companyName === CONSTANTS.VINTE_COMPANY_NAME) {
        if (rolName === CONSTANTS.ADMIN) {
          // get conversations by admin's channelsId
          fetchedConversations = await getConversations(
            selectedFilters,
            userId,
            isAdminOfIds
          );

          // get agents conversations
          const agentFilters = { ...selectedFilters };

          agentFilters.agents = [{ id: actualAgentId }];
          if (
            selectedFilterType !== CONSTANTS.FILTER_ASSIGNED &&
            selectedFilters.agents?.length === 0
          ) {
            try {
              const agentByPlace = await client.query({
                query: ApolloAgents.GET_AGENTS_NOT_SUPERADMINS_BY_SPACE_ID,
                variables: {
                  spaceId: selectedSpaces?.id,
                },
              });
              if (agentByPlace?.data?.users?.length > 0) {
                agentFilters.agents = [
                  { id: actualAgentId },
                  ...agentByPlace?.data?.users
                    ?.filter((x) => x.rol?.name !== CONSTANTS.SUPERADMIN)
                    ?.map((x) => ({ id: x.id })),
                ];
              }
            } catch (error) {
              console.error(error);
            }
          }
          agentFilters.isAgent = true;
          let fetchedConversationsAgent = [];
          if (
            selectedFilterType !== CONSTANTS.FILTER_ASSIGNED &&
            selectedFilters.agents?.length === 0
          ) {
            fetchedConversationsAgent = await getConversations(
              agentFilters,
              userId,
              isAdminOfIds
            );
          }
          fetchedConversations = [
            ...fetchedConversations,
            ...fetchedConversationsAgent,
          ];
          fetchedConversations = uniqBy(fetchedConversations, "id");
          fetchedConversations = orderBy(
            fetchedConversations,
            [(x) => moment(x.lastMessage?.createdAt)],
            ["desc"]
          );
        } else {
          fetchedConversations = await getConversations(
            selectedFilters,
            userId,
            isAdminOfIds
          );
        }
      } else {
        fetchedConversations = await getConversations(selectedFilters, userId, isAdminOfIds);
      }

      if (spaceChanged()) {
        await leaveToSelectedSpace();
        return;
      }
      if (gaveWayToNewerRestart()) return;

      // Paso extra: para la company/channel/rol restringidos, el agente solo ve
      // las conversaciones asignadas a él. No afecta a ningún otro usuario.
      const restrictedUser = getState().user.data;
      fetchedConversations = fetchedConversations.filter(
        (conversation) =>
          !shouldHideUnassignedConversation(
            restrictedUser,
            conversation?.channel?.id,
            conversation?.agent?.id
          )
      );

      let newConversations = [];
      if (!restart) {
        // A pinned row the server has just returned is a real result from
        // here on, so it counts for the next offset (`uniqBy` below keeps
        // this first copy and drops the server's).
        const fetchedIds = new Set(fetchedConversations.map((c) => c?.id));
        const settled = conversations.map((c) =>
          c?.outsideResults && fetchedIds.has(c.id)
            ? { ...c, outsideResults: false }
            : c
        );
        newConversations = [...settled, ...fetchedConversations];
      } else newConversations = [...fetchedConversations];
      
      if (cconversation?.id) {
        // Se refresca la conversación con lo que acaba de llegar del servidor,
        // cuando la bandeja la trajo.
        //
        // El snapshot de `openedConversation` es de ANTES de la caída, así que
        // `agent`, `tags` y sobre todo `whatsappStartedAt` pueden ir viejos —
        // y `setConversationAsSelectedAction` recalcula `isCurrentWindowOpen`
        // con ese `whatsappStartedAt`. Errar ahí hacia "ventana cerrada"
        // obliga al asesor a mandar plantilla sin necesidad.
        //
        // OJO con la forma: se MEZCLA, no se sustituye. La lista de la bandeja
        // es una proyección con menos campos que la conversación completa
        // (`getConversations` no devuelve lo mismo que `getConversationID` ni
        // que lo que había en `current`). Cambiar una por otra a secas perdía
        // campos que sí teníamos — el test "(a) con la consulta de la
        // conversación CAÍDA" lo cazó al quedarse sin `name`. Mezclando, la
        // base sigue siendo la copia completa y encima se pisan solo los
        // campos que el servidor acaba de confirmar.
        const recienTraida = fetchedConversations.find(
          (c) => c.id === cconversation.id
        );
        const fresh = recienTraida
          ? { ...cconversation, ...recienTraida }
          : cconversation;
        dispatch(setConversationAsSelectedAction(fresh));
        dispatch(getMessagesAction(false, fresh));
      }

      if (fetchedConversations?.length === 0) {
        dispatch({ type: THERE_IS_NO_MORE_CONVERSATIONS });
      } else dispatch({ type: THERE_IS_MORE_CONVERSATIONS });

      newConversations = uniqBy(newConversations, "id");

      const newConversationsWithLastMessage = newConversations.filter(
        (c) => c.lastMessageId !== null
      );

      dispatch({
        type: GET_CONVERSATIONS_SUCCESS,
        payload: {
          conversations:
            conversationsFilter?.length > 0
              ? newConversations
              : newConversationsWithLastMessage,
          source: CONSTANTS.CONVERSATIONS_SOURCE_REQUEST,
        },
      });
      return;
    } catch (error) {
      if (spaceChanged()) {
        await leaveToSelectedSpace();
        return;
      }
      if (gaveWayToNewerRestart()) return;

      dispatch({ type: GET_CONVERSATIONS_ERROR, payload: { error } });

      // `GET_CONVERSATIONS_ERROR` deja `current: {}` (ver el reducer), así que
      // hasta aquí una sola reconexión con la bandeja fallando todavía le
      // cerraba el chat al asesor — el mismo síntoma que el resto de este PR
      // arregla, entrando por el camino de error. Se repone lo que estuviera
      // abierto.
      //
      // Va DESPUÉS del dispatch del error, no antes: el orden importa, porque
      // el reducer del error borra `current` y pisaría la reposición.
      //
      // Sobre la frescura: esta copia es la de ANTES de la caída, así que
      // `agent`, `tags` y `whatsappStartedAt` pueden ir viejos. Es aceptable
      // porque el fallo es conservador — `setConversationAsSelectedAction`
      // recalcula `isCurrentWindowOpen` con ese `whatsappStartedAt`, y errar
      // hacia "ventana cerrada" obliga a usar plantilla, que es molesto pero
      // no manda nada indebido. Y aquí no hay alternativa: la consulta que
      // habría traído la copia fresca es justamente la que acaba de fallar.
      //
      // Only a conversation of the selected space is put back. After a space
      // switch the one captured above is the previous space's, and the chat
      // stays closed as it does when the reload succeeds.
      if (
        openedConversation?.id &&
        locateConversationSpace(openedConversation, getState().spaces)
          .inSelectedSpace
      ) {
        dispatch(setConversationAsSelectedAction(openedConversation));
      }
    }
  };

export const updateMessageStatus = (data) => (dispatch, getState) => {
  const statuses = {
    null: 0,
    sent: 0,
    delivered: 1,
    viewed: 2,
    deleted: 3,
    failed: 3,
    warning: 3,
  };

  const conversations = [...getState().conversations.data];
  const messages = [...getState().messages.data];

  if ((data.intents || 0) < 10) {
    let updateMessageSuccess = false;
    messages.forEach((message) => {
      if (
        message.id === data.id &&
        statuses[data.messageStatus] > (statuses[message.messageStatus] || 0)
      ) {
        dispatch(
          updateMessageStatusId(
            data.id,
            data.messageStatus,
            messages,
            data.errorCode,
            data.errorInfo
          )
        );
        updateMessageSuccess = true;
      }
    });

    if (!updateMessageSuccess) {
      if (data.intents) {
        data.intents = data.intents += 1;
      } else {
        data.intents = 1;
      }
      setTimeout(() => dispatch(updateMessageStatus(data)), 200);
    }
  }

  if ((data.conversationIntents || 0) < 10) {
    let updateConversationSuccess = false;
    conversations.forEach(async (conversation) => {
      //console.log('statuses[data.messageStatus] => ', data.messageStatus)
      //console.log('conversation.lastMessage?.messageStatus => ', conversation.lastMessage?.messageStatus)

      // A row marked failed by this client (`updateMessageWithError`) holds a
      // guess, not a status the server reported: it does not outrank what the
      // server says about that same message.
      const currentRank = isLocalSendFailure(conversation?.lastMessage)
        ? 0
        : statuses[conversation?.lastMessage?.messageStatus] || 0;
      if (
        conversation?.lastMessage?.id === data.id &&
        statuses[data.messageStatus] >= currentRank
      ) {
        //console.log('statuses[data.messageStatus] => ', data.messageStatus)

        dispatch(
          updateLastMessage(
            data.clientId,
            data.id,
            data.messageStatus,
            data.errorCode,
            data.errorInfo,
            data.windowExpiration
          )
        );
        updateConversationSuccess = true;
      }
    });

    if (!updateConversationSuccess) {
      if (data.intents) {
        data.conversationIntents = data.conversationIntents += 1;
      } else {
        data.conversationIntents = 1;
      }
      setTimeout(() => dispatch(updateMessageStatus(data)), 200);
    }
  }
};

// Validación extra exclusiva para una company + channel + rol (agente)
// específicos: estos usuarios solo deben ver conversaciones asignadas a ellos.
// Devuelve true cuando la conversación NO debe mostrarse. Para cualquier otro
// usuario, company o channel devuelve false (no afecta su comportamiento).
const shouldHideUnassignedConversation = (user, channelId, agentId) => {
  const isRestrictedUser =
    user?.company?.id === CONSTANTS.ASSIGNED_ONLY_COMPANY_ID &&
    user?.rol?.id === CONSTANTS.ASSIGNED_ONLY_ROLE_ID;
  if (!isRestrictedUser) return false;
  if (channelId !== CONSTANTS.ASSIGNED_ONLY_CHANNEL_ID) return false;
  return agentId !== user?.id;
};

const isConversationInFilters =
  (conversation, options = {}) => async (dispatch, getState) => {
    const selectedFilters = getState().filters.selected;
    const usersRef = getState().conversations.allUsersForFilter;
    const user = getState().user.data;

    // Paso extra: ocultar conversaciones no asignadas al usuario para la
    // company/channel/rol restringidos. No afecta a ningún otro caso.
    if (
      shouldHideUnassignedConversation(
        user,
        conversation?.client?.channel?.id,
        conversation?.client?.agent?.id
      )
    ) {
      return false;
    }

    const selectedChannels = getState().spaces?.selected?.channels;
    if (user?.company?.name === CONSTANTS.VINTE_COMPANY_NAME) {
      if (user?.rol?.name !== CONSTANTS.AGENT_PERMISSION) {
        let isChannelsFilterPassed = undefined;
        if (
          user.spaces.length === 1 &&
          user.spaces[0].name === CONSTANTS.VINTE_DEFAULT_NAME
        ) {
          isChannelsFilterPassed = [
            CONSTANTS.VINTE_DEFAULT_WHATSAPP,
            CONSTANTS.VINTE_DEFAULT_FACEBOOK,
          ].includes(conversation?.client?.channel?.id);
          if (isChannelsFilterPassed === false) return false;
        } else {
          isChannelsFilterPassed = selectedChannels?.find(
            (x) => x.id === conversation?.client?.channel?.id
          );
        }
        if (isChannelsFilterPassed === undefined) {
          if (user?.rol?.name === CONSTANTS.ADMIN) {
            let userInSpaceHasLead = false;
            let users = [];
            if (usersRef?.length === 0) {
              const users = await dispatch(getAllAgents());
              dispatch({ type: SET_USER_FILTERS, payload: users });
            } else {
              users = [...usersRef];
            }
            const usersInSameSpace = users
              .filter((x) => x.rol?.name !== CONSTANTS.SUPERADMIN)
              .filter((x) => {
                const isInSameSpace = intersectionWith(
                  user.spaces,
                  x.spaces,
                  (a, b) => a.id === b.id
                );
                if (isInSameSpace?.length > 0) return true;
              });
            usersInSameSpace.forEach((x) => {
              if (x.id === conversation?.client?.agent?.id) {
                userInSpaceHasLead = true;
              }
            });
            if (userInSpaceHasLead === false) return false;
          } else {
            return false;
          }
        }
      }
    } else {
      const isChannelsFilterPassed = selectedChannels?.find(
        (x) => x.id === conversation?.client?.channel?.id
      );
      if (isChannelsFilterPassed === undefined) return false;
    }

    if (selectedFilters?.channels?.length > 0) {
      const filterPlatforms = [...selectedFilters.channels];
      if (filterPlatforms?.length > 0) {
        const isFilterPlatformPassed = filterPlatforms?.find(
          (x) => x?.platform?.name === conversation.client?.platform?.name
        );
        if (isFilterPlatformPassed === undefined) return false;
      }
    }

    if (selectedFilters?.agents?.length > 0) {
      const filterAgents = [...selectedFilters.agents];
      if (filterAgents?.length > 0) {
        const isFilterAgentsPassed = filterAgents?.find(
          (x) => x.id === conversation.client?.agent?.id
        );
        if (isFilterAgentsPassed === undefined) return false;
      }
    }

    if (selectedFilters?.tags?.length > 0) {
      const filterTags = [...selectedFilters.tags];
      if (filterTags?.length > 0) {
        const isFilterTagsPassed = intersectionWith(
          filterTags,
          conversation.client?.tags,
          (a, b) => a.id === b.id
        );
        if (isFilterTagsPassed?.length === 0) return false;
      }
    }

    const answeredFilter = selectedFilters.noAnswered;
    if (conversation?.client?.answered === answeredFilter) return false;
    if (
      conversation?.client?.lastMessage?.sender !== "CLIENT" &&
      answeredFilter
    )
      return false;

    if (selectedFilters.isArchived === undefined) {
      if (conversation?.client?.isArchived) return false;
    } else if (!conversation?.client?.isArchived) return false;

    if (selectedFilters.isArchivedBroadcast === undefined) {
      if (
        conversation?.client?.isBroadcastMessage &&
        conversation?.client?.plataform?.name === "SMS"
      )
        return false;
    } else if (
      !conversation?.client?.isBroadcastMessage &&
      conversation?.client?.plataform?.name === "SMS"
    )
      return false;

    // if (selectedFilters.isConversationValuable === undefined) {
    //   if (conversation?.client?.isConversationValuable) return false;
    // } else if (!conversation?.client?.isConversationValuable) return false;

    // const unreadFilter = selectedFilters.unreadMessages;
    // if (conversation?.client?.lastMessage?.seen === unreadFilter) return false;
    // console.log("filter 9");

    const assignedFilter = getState().filters.type;
    const userId = getState().user?.data?.id;
    if (assignedFilter === "assigned") {
      if (conversation?.client?.agent?.id !== userId) return false;
    }

    // Skip search filter check for subscription updates to prevent
    // conversations from disappearing when user has an active search
    if (!options.skipSearchFilter) {
      const searchValue = `${getState()
        .conversations.searchFilter?.trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s/g, "")
        .toLowerCase()}`;

      if (searchValue) {
        if (!conversation?.client?.cleanedName?.includes(searchValue))
          return false;
      }
    }

    return true;
  };

// `options.insertOnly`: the caller only brings a row the list is missing (the
// task focus and modo cola helpers), it is not a Pusher event. A row the
// filters reject then stays out of the list without closing the open
// conversation (spec S16: an archived conversation opened from a task).
export const updateConverations = (data, options = {}) => async (dispatch, getState) => {
  // Modo cola (R7): a restricted agent's inbox holds only its seats when there
  // is no search text, so events for conversations outside the seat set are
  // dropped instead of inserted. No-op when the feature is off.
  if (shouldIgnoreRealtimeConversation(getState(), data?.client?.id)) return;

  const conversationsData = [...getState().conversations.data];
  const currentConversation = { ...getState().conversations.current };
  const userId = getState().user.data?.id;
  const rolName = getState().user.data.rol.name;
  const selectedSpaces = getState().spaces.selected;
  const isAdminOfIds = getState().user.data.isAdminOfIds;
  const channelIds = selectedSpaces?.channels?.map((c) => c.id);
  let shouldUpdateSelectedConversation = false;

  // Paso extra (después de la notificación de Pusher): exclusivo para la
  // company/channel/rol restringidos. A veces el agente recibe eventos de
  // conversaciones que no tiene asignadas. Verificamos con la información que
  // ya trae la notificación de Pusher (sin consultar el backend) que la
  // conversación esté asignada a este agente. Si no lo está, no se muestra y,
  // si ya estuviera en la lista, se elimina. No afecta a nadie más.
  const isAssignedOnlyAgent =
    getState().user.data?.company?.id === CONSTANTS.ASSIGNED_ONLY_COMPANY_ID &&
    getState().user.data?.rol?.id === CONSTANTS.ASSIGNED_ONLY_ROLE_ID &&
    data?.client?.channel?.id === CONSTANTS.ASSIGNED_ONLY_CHANNEL_ID;

  if (isAssignedOnlyAgent && data?.client?.agent?.id !== userId) {
    dispatch(deleteConversationAction(data));
    return;
  }

  if (userId && isAdminOfIds && isAdminOfIds.length > 0) {
    if (!data?.client?.agent?.id) {
      return;
    }
    if (data?.client?.agent?.id && !isAdminOfIds.includes(data?.client?.agent?.id)) {
      return;
    }
  }

  if (!channelIds.some((id) => id === data?.client?.channel?.id)) {
    return;
  }

  if (rolName === CONSTANTS.GROUPADMIN) {
    if (data?.client?.groupAdminId !== userId) {
      return;
    }

    if (!data?.client?.groupAdminId && data?.client?.agent?.id !== userId) {
      if (data?.type === "newClient") {
        dispatch(deleteConversationAction(data));
      }
      return;
    }

    // if (data?.client?.agent?.id === userId) {
    //   console.log('it does not belong to user group or agentId')
    //   return;
    // };
  }

  // Skip search filter for subscription updates to prevent conversations from
  // disappearing when user has an active search and receives messages from mobile app
  const conversationPassFilters = await dispatch(isConversationInFilters(data, { skipSearchFilter: true }));
  if (!conversationPassFilters) {
    if (options.insertOnly) return;
    dispatch(deleteConversationAction(data));
    return;
  }

  if (rolName === CONSTANTS.GROUPADMIN) {
    if (
      data?.client?.groupAdminId === null &&
      data?.client?.agent?.id !== userId &&
      data?.type === "newClient"
    ) {
      dispatch(deleteConversationAction(data));
      return;
    }
  }

  if (data.message?.senderId === userId) return;

  let newConversationsData = [...conversationsData];
  let conversationIndex;
  if (data.client.platform.name === "SMS") {
    conversationIndex = newConversationsData.findIndex(
      (x) => data.client.senderId === x.senderId
    );
  } else {
    conversationIndex = newConversationsData.findIndex(
      (x) => data.client.id === x.id
    );
  }
  // Modo cola: read BEFORE the row's lastMessage is overwritten below.
  const alreadyCounted = isRealtimeMessageAlreadyCounted(
    getState(),
    newConversationsData[conversationIndex],
    data
  );
  if (conversationIndex > 0) {
    const switchConversation = newConversationsData[conversationIndex];
    switchConversation.tags = [...(data?.client?.tags || [])];
    switchConversation.isConversationValuable = data?.client?.isConversationValuable;
    switchConversation.lastMessage = {
      ...data?.client?.lastMessage,
      time: parseInt(`${data?.client?.lastMessage?.time}000`, 10),
    };
    if (data?.client?.agent?.id) {
      switchConversation.agent = { ...data?.client.agent };
    }
    const seal = pusherSealOf(data);
    if (seal) switchConversation.prov = seal;
    if (data.client.id === currentConversation?.id) {
      dispatch(seenMessage(currentConversation));
    } else {
      if (data.client.lastMessage?.sender === CONSTANTS.CLIENT_SENDER) {
        if (!alreadyCounted) switchConversation.unreadMessages += 1;
      } else {
        switchConversation.unreadMessages = data.client?.unreadMessages;
      }
    }

    if (data?.client?.isConversationValuable !== switchConversation?.isConversationValuable) {
      switchConversation.isConversationValuable = data?.client?.isConversationValuable;
      shouldUpdateSelectedConversation = true;
    }

    newConversationsData.splice(conversationIndex, 1);
    newConversationsData.unshift(switchConversation);
  } else if (conversationIndex === 0) {
    newConversationsData[conversationIndex].lastMessage = {
      ...data?.client?.lastMessage,
      time: parseInt(`${data?.client?.lastMessage?.time}000`, 10),
    };
    newConversationsData[conversationIndex].tags = [...(data?.client?.tags || [])];
    newConversationsData[conversationIndex].isConversationValuable = data?.client?.isConversationValuable;

    if (data?.client?.agent?.id) {
      newConversationsData[conversationIndex].agent = { ...data?.client.agent };
    }
    const seal = pusherSealOf(data);
    if (seal) newConversationsData[conversationIndex].prov = seal;

    if (data?.client?.isConversationValuable !== newConversationsData?.[conversationIndex]?.isConversationValuable) {
      newConversationsData[conversationIndex].isConversationValuable = data?.client?.isConversationValuable;
      shouldUpdateSelectedConversation = true;
    }

    if (data.client.id === currentConversation?.id) {
      dispatch(seenMessage(currentConversation));
    } else {
      if (data.client.lastMessage?.sender === CONSTANTS.CLIENT_SENDER) {
        if (!alreadyCounted) newConversationsData[conversationIndex].unreadMessages += 1;
      } else {
        newConversationsData[conversationIndex].unreadMessages = data.client?.unreadMessages;
      }
    }
  } else {
    // Conversations already in the list came from the search-filtered query,
    // but a brand-new insert must match the active search too — otherwise any
    // incoming message leaks unrelated contacts into the filtered results
    const { searchFilter } = getState().conversations;
    const searchValue = searchFilter?.trim() ? cleanString(searchFilter) : "";
    // Fallback mirrors the backend cleanedName (name + phone) so phone-number
    // searches still match payloads that arrive without cleanedName
    const incomingCleanedName =
      data.client?.cleanedName ||
      cleanString(
        `${data.client?.nameFromChannel || data.client?.name || ""}${data.client?.senderId || data.client?.phoneNumber || ""}`
      );

    // The open conversation is the exception: the agent is looking at it, so
    // its events must reach the list even when the name does not match the
    // search — otherwise it never shows up while the search is active.
    const isOpenConversation =
      data.client.id === currentConversation?.id ||
      (data.client.platform.name === "SMS" &&
        data.client.senderId === currentConversation?.senderId);

    const matchesSearch =
      !searchValue || incomingCleanedName.includes(searchValue);

    if (matchesSearch || isOpenConversation) {
      newConversationsData = [
        {
          ...data.client,
          // The agent is reading the open conversation: its row is not
          // unread, and the backend is told so below, as the branches for
          // rows already in the list do.
          unreadMessages: isOpenConversation ? 0 : 1,
          prov: pusherSealOf(data),
          // Inserted only because it is open: the server would not return it
          // for this search, so `getConversationsAction` must not count it
          // in the next page's offset.
          ...(matchesSearch ? {} : { outsideResults: true }),
        },
        ...newConversationsData,
      ];
      newConversationsData[0].lastMessage = {
        ...data?.client?.lastMessage,
        time: parseInt(`${data?.client?.lastMessage?.time}000`, 10),
      };

      if (data?.client?.agent?.id) {
        newConversationsData[0].agent = { ...data?.client.agent };
      }

      if (isOpenConversation) {
        dispatch(seenMessage(currentConversation));
      }
    }
  }

  if (shouldUpdateSelectedConversation) {
    const newSelectedConversation = newConversationsData.find(x => x.id === currentConversation?.id);
    if (newSelectedConversation) {
      newSelectedConversation.isConversationValuable = data?.client?.isConversationValuable;
    }
    const payload = {
      data: newConversationsData,
      current: newSelectedConversation,
    };
    // Labelled at the dispatch site: this list was rewritten by a Pusher event
    // (newClient / newMessage), which the action type alone cannot say.
    dispatch(
      withTelemetrySource(
        { type: SET_CONVERSATION_AS_SELECTED, payload },
        TELEMETRY_SOURCES.PUSHER
      )
    );
  } else {
    dispatch(
      withTelemetrySource(
        { type: SET_CONVERSATIONS, payload: newConversationsData },
        TELEMETRY_SOURCES.PUSHER
      )
    );
  }

  if (
    data.client.id === currentConversation?.id ||
    (data.client.platform.name === "SMS" &&
      data.client.senderId === currentConversation?.senderId)
  ) {
    let messages = [...getState().messages.data];
    data.client.lastMessage.client = data.client;
    // A customer message reopens the 24-hour window of the open conversation.
    if (
      data.client.id === currentConversation?.id &&
      data.client.lastMessage.sender === CONSTANTS.CLIENT_SENDER
    ) {
      dispatch(
        setWindowLastClientMessageAction(
          data.client.id,
          data.client.lastMessage.createdAt
        )
      );
    }
    if (data.client.lastMessage.sender === "CLIENT") {
      messages = [
        {
          ...data?.client?.lastMessage,
          time: parseInt(`${data?.client?.lastMessage?.time}000`, 10),
        },
        ...messages,
      ];
    } else {
      const foundMessageIndex = messages.findIndex(
        (x) => x.id === data?.client?.lastMessage?.id
      );
      if (foundMessageIndex === -1) {
        messages = [
          {
            ...data?.client?.lastMessage,
            time: parseInt(`${data?.client?.lastMessage?.time}000`, 10),
          },
          ...messages,
        ];
      } else {
        messages[foundMessageIndex] = {
          ...data?.client?.lastMessage,
          time: parseInt(`${data?.client?.lastMessage?.time}000`, 10),
        };
      }
    }
    const messagesFiltered = messages.filter((x) => !x.id.includes("fakeId"));
    const messagesUniq = uniqBy(messagesFiltered, "id");
    dispatch({ type: ADD_NEW_MESSAGES, payload: messagesUniq });
  }

  if (data?.botAssigments?.id) {
    if (userId === data?.botAssigments?.nextAgent) {
      const currentAssignments = getState().app.assignments;
      const indexAssignment = currentAssignments.findIndex(
        (as) => as.id === data?.botAssigments?.id
      );
      if (indexAssignment !== -1) currentAssignments.splice(indexAssignment, 1);
      dispatch(setAppStatus([...currentAssignments, data?.botAssigments]));
    }
  }
};

export const addConversationsAction =
  (conversations) => async (dispatch, getState) => {
    // Read state at dispatch time (latest value, not stale closure).
    // Use uniqBy at the end to handle concurrent calls that both read the
    // same snapshot before either has written — ensures no duplicate keys.
    const currentConversations = getState().conversations.data;
    const restrictedUser = getState().user.data;
    const incoming = conversations
      .filter(
        (c) =>
          !shouldHideUnassignedConversation(
            restrictedUser,
            c?.channel?.id,
            c?.agent?.id
          )
      )
      .map((c) => ({
        ...c,
        platform: c.channel?.platform,
        unreadMessages: c.unreadMessagesCount,
        prov: normalizeSeal(c.prov),
      }));
    const merged = uniqBy([...currentConversations, ...incoming], 'id');
    // Its only caller is the Board view, which fetches with its own query.
    dispatch(
      withTelemetrySource(
        { type: SET_CONVERSATIONS, payload: merged },
        TELEMETRY_SOURCES.BOARD
      )
    );
  };

export const finishBotAssignment = (data) => async (dispatch, getState) => {
  console.log("FINISH BOT ASSIGNMENT ===> ");
  const currentAssignments = getState().app.assignments;

  const index = currentAssignments.findIndex(
    (as) => as?.id === data?.botConfig?.id && as?.clientId === data?.client?.id
  );

  if (index !== -1) currentAssignments.splice(index, 1);
  dispatch(setAppStatus([...currentAssignments]));
};

export const removeAssignation = (clientId) => async (dispatch, getState) => {
  console.log("NEW BOT unassign ===> ", clientId);

  const currentAssignments = [...getState().app.assignments];

  const indexAssignment = currentAssignments.findIndex(
    (as) => as?.clientId === clientId
  );
  currentAssignments.splice(indexAssignment, 1);

  await dispatch(setAppStatus(currentAssignments));
};

// Pusher `unassignClient`: every list write below is that event (labelled
// `pusher`), though none of them inserts a row — they rewrite or drop one.
export const unassignClient = (data) => async (dispatch, getState) => {
  const userId = getState().user.data.id;
  const companyName = getState().user.data.company?.name;

  if (userId != data?.client?.agent?.id)
    await dispatch(removeAssignation(data?.client?.id));

  const userRol = getState().user.data.rol.name;
  if (userRol === CONSTANTS.SUPERADMIN || userRol === CONSTANTS.ADMIN) {
    if (
      companyName === CONSTANTS.VINTE_COMPANY_NAME &&
      userRol === CONSTANTS.ADMIN
    ) {
      // TODO: Review this case
      const conversationsData = [...getState().conversations.data];
      const newConversations = conversationsData.map((x) => {
        if (x.id === data.client?.id) {
          return {
            ...x,
            agent: {
              id: data.client?.agent?.id,
              name: data?.client?.agent?.name,
            },
          };
        }
        return { ...x };
      });
      dispatch(
        withTelemetrySource(
          { type: SET_CONVERSATIONS, payload: newConversations },
          TELEMETRY_SOURCES.PUSHER
        )
      );

      const selectedConversation = { ...getState().conversations.current };
      if (selectedConversation?.id === data?.client?.id) {
        const newConversation = {
          ...selectedConversation,
          agent: {
            id: data.client?.agent?.id,
            name: data?.client?.agent?.name,
          },
        };
        dispatch({ type: SET_CURRENT_CONVERSATION, payload: newConversation });
      }
    } else {
      const conversationsData = [...getState().conversations.data];
      const newConversations = conversationsData.map((x) => {
        if (x.id === data.client?.id) {
          return {
            ...x,
            agent: {
              id: data.client?.agent?.id,
              name: data?.client?.agent?.name,
            },
          };
        }
        return { ...x };
      });
      dispatch(
        withTelemetrySource(
          { type: SET_CONVERSATIONS, payload: newConversations },
          TELEMETRY_SOURCES.PUSHER
        )
      );

      const selectedConversation = { ...getState().conversations.current };
      if (selectedConversation?.id === data?.client?.id) {
        const newConversation = {
          ...selectedConversation,
          agent: {
            id: data.client?.agent?.id,
            name: data?.client?.agent?.name,
          },
        };
        dispatch({ type: SET_CURRENT_CONVERSATION, payload: newConversation });
      }
    }
  } else {
    const conversationsData = [...getState().conversations.data];
    const currentConversationId = getState().conversations.current?.id;
    const newConversationsData = conversationsData.filter(
      (x) => x.id !== data.client.id
    );
    dispatch(
      withTelemetrySource(
        { type: SET_CONVERSATIONS, payload: newConversationsData },
        TELEMETRY_SOURCES.PUSHER
      )
    );
    if (data.client.id === currentConversationId) {
      dispatch({ type: ADD_NEW_MESSAGES, payload: [] });
      dispatch({ type: CLEAR_SELECTED_CONVERSATION });
    }
  }
};

export const updateAssignTag = () => (dispatch, getState) => {
  const tags = getState().tags.data;
  const assigmentTag = tags?.find((x) => x.name === "Reasignada");
  if (assigmentTag?.id) {
    const conversation = getState().conversations?.current;
    const payload = conversation?.tags?.map((x) => x);
    payload.push(assigmentTag);
    dispatch({ type: UPDATE_REASSING_TAG, payload });
  }
};

export const markAsReadAction = (data) => (dispatch, getState) => {
  const conversationsData = getState().conversations.data;
  let update = false;
  const newConversations = conversationsData.map((x) => {
    if (x.id === data.clientId) {
      update = true;
      return { ...x, unreadMessages: 0 };
    }
    return { ...x };
  });
  if (update) {
    dispatch({ type: SET_CONVERSATIONS, payload: newConversations });
  }
};

export const markAsConversationValuable = (data) => (dispatch, getState) => {
  const conversationsData = getState().conversations.data;
  let update = false;
  const newConversations = conversationsData.map((x) => {
    if (x.id === data?.client?.id) {
      update = true;
      return {
        ...x,
        isConversationValuable: data?.client?.isConversationValuable,
      };
    }
    return { ...x };
  });
  if (update) {
    const value = data?.client?.isConversationValuable;
    dispatch({
      type: UPDATE_VALUABLE_CLASSIFICATION,
      payload: { value, data: newConversations },
    });
  }
};

export const markAsUnReadAction = (data) => (dispatch, getState) => {
  const conversationsData = getState().conversations.data;
  let update = false;
  console.log("markAsUnReadAction", data);
  const newConversations = conversationsData.map((x) => {
    if (x.id === data.client?.id) {
      update = true;
      return { ...x, unreadMessages: 1 };
    }
    return { ...x };
  });
  if (update) {
    dispatch({ type: SET_CONVERSATIONS, payload: newConversations });
  } else {
    newConversations.unshift({
      ...data.client,
      unreadMessages: 1,
      prov: pusherSealOf(data),
    });
    newConversations[0].lastMessage = {
      ...data.message,
      time: parseInt(`${data.message?.time}000`, 10),
    };
    // The one branch of this handler that INSERTS a conversation straight from
    // the Pusher payload; the branch above only rewrites a row already there.
    dispatch(
      withTelemetrySource(
        { type: SET_CONVERSATIONS, payload: newConversations },
        TELEMETRY_SOURCES.PUSHER
      )
    );
  }
};

export const setConversationsAction = (conversations) => ({
  type: SET_CONVERSATIONS,
  payload: conversations,
});

export const setCurrentWindowOpen = () => (dispatch, getState) => {
  dispatch({
    type: SET_CURRENT_WINDOW_OPEN,
    payload: {},
  });
};

export const setSearchFilterAction = (input) => ({
  type: SET_SEARCH_FILTER,
  payload: input,
});

export const setConversationAsSelectedAction =
  (conversation) => async (dispatch, getState) => {
    try {
      dispatch({ type: HANDLE_CLIENT_INFO, payload: true });
      dispatch(handleNewMessagesButtonAction(false));
      let isCurrentWindowOpen;
      const conversations = [...getState().conversations.data];
      const conversationAsSelected = conversations.map((x) => {
        if (x.id === conversation.id) {
          isCurrentWindowOpen = isDialogWindowOpen(
            conversation.whatsappStartedAt
          );
          return { ...x, selected: true, unreadMessages: 0, hasUpdates: false };
        }
        return { ...x, selected: false };
      });

      // const lastSurveyInfo = await getLastSurveyInfo(conversation?.id);
      //console.log('lastSurveyInfo ===========> ', lastSurveyInfo)
      dispatch({
        type: SET_CONVERSATION_AS_SELECTED,
        payload: {
          data: conversationAsSelected,
          // current: { ...conversation, lastSurveyInfo: lastSurveyInfo },
          current: { ...conversation },
          isCurrentWindowOpen,
        },
      });
    } catch (error) {
      console.error(error);
      dispatch({ type: GET_CONVERSATIONS_ERROR, payload: { error } });
    }
  };

export const getConversationLastSurveyInfo =
  (conversationId) => async (dispatch) => {
    try {
      console.log("GETTING LAST SURVEY INFO", conversationId);
      const lastSurveyInfo = await getLastSurveyInfo(conversationId);
      console.log("LAST SURVEY INFO", lastSurveyInfo);
      dispatch({
        type: SET_LAST_SURVEY_INFO,
        payload: lastSurveyInfo,
      });
    } catch (error) {
      console.error(error);
    }
  };

export const handleClientInfoAction =
  (status) => async (dispatch, getState) => {
    try {
      if (status) {
        const selectedConversationId = getState().conversations.current.id;
        const clientInfoData = await getClientInfo(selectedConversationId);
        dispatch({ type: UPDATE_CLIENT_INFO, payload: clientInfoData });
      }
      dispatch({ type: HANDLE_CLIENT_INFO, payload: status });
    } catch (error) {
      console.error(error);
      return { error };
    }
  };

export const updateClientInfoAction =
  (clientInfo) => async (dispatch, getState) => {
    try {
      const clientUpdated = await updateClientInfo(clientInfo);
      if (clientUpdated) {
        const conversationData = [...getState().conversations.data];
        const index = conversationData.findIndex((x) => x.id === clientInfo.id);
        conversationData[index] = { ...conversationData[index], ...clientInfo };
        dispatch({ type: SET_CONVERSATIONS, payload: conversationData });
        return true;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  };


export const createClientInfoAction =
  (clientInfo, agentId, importContact, updateProgress) =>
  async (_, getState) => {
    try {
      const companyId = getState().user.data.company.id;
      // Para la compañía específica, obtener spaceId del espacio seleccionado
      let spaceId = null;
      if (companyId === CONSTANTS.SPACE_FILTERED_COMPANY_ID) {
        spaceId = getState().spaces?.selected?.id || null;
      }
      const createClient = await createClientFromPhone(
        clientInfo,
        agentId,
        importContact,
        updateProgress,
        companyId,
        spaceId
      );

      return createClient;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };
export const createClientInfoFromExcelAction =
  (clientInfo, agentId, importContact) => async (_, getState) => {
    try {
      const companyId = getState().user?.data?.company?.id;
      // Para la compañía específica, obtener spaceId del espacio seleccionado
      let spaceId = null;
      if (companyId === CONSTANTS.SPACE_FILTERED_COMPANY_ID) {
        spaceId = getState().spaces?.selected?.id || null;
      }
      const createClient = await createClientFromPhone(
        clientInfo,
        agentId,
        importContact,
        null,
        companyId,
        spaceId
      );
      return createClient;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const getPlatformApi = async (companyId) => {
  try {
    return await getPlatformApiFromPhone(companyId);
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const updateClientAgent =
  (agentId, clientId, isGroupAdmin = false, forceAssign = false) =>
  async (dispatch, getState) => {
    try {
      const data = { clientId, agentId, isGroupAdmin };
      // Modo cola (F2): only an admin's confirmed override sends the key, so
      // every other reassign keeps the exact same request body.
      if (forceAssign === true) data.forceAssign = true;
      const conversations = await getState().conversations.data;
      const reassignResponse = await assignAgent(data);
      let newSelectedConversation;
      const newConversations = map(conversations, (conversation) => {
        if (conversation.id === clientId) {
          conversation.agent = reassignResponse.assignedAgent;
          conversation.groupAdminId = reassignResponse?.groupAdmin;
          newSelectedConversation = conversation;
        }
        return conversation;
      });

      if (newSelectedConversation?.id) {
        const payload = {
          data: newConversations,
          current: newSelectedConversation,
        };
        dispatch({ type: SET_CONVERSATION_AS_SELECTED, payload });
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const changeCategoryAction =
  (category, clientId) => async (dispatch, getState) => {
    try {
      const currentConversation = getState().conversations.current;
      const conversations = getState().conversations.data;
      if (currentConversation.id === clientId) {
        currentConversation.category = category;
      }
      const newConversations = map(conversations, (conversation) => {
        if (conversation.id === clientId) {
          conversation = currentConversation;
        }
        return conversation;
      });
      await changeCategory(clientId, category.id);
      const payload = { data: newConversations, current: currentConversation };
      dispatch({ type: SET_CONVERSATION_AS_SELECTED, payload });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const updateClientTags = (data) => async (dispatch, getState) => {
  try {
    const conversations = [...getState().conversations.data];
    const selectedConversation = { ...getState().conversations.current };
    const tags = getState().tags.data;
    let updateTags = false;
    const newConversations = conversations.map((x) => {
      if (x.id === data.id) {
        if (data.tags.connect.length > 0) {
          console.log("ADDING TAGS");
          console.log("data.tags.connect ====> ", data.tags.connect);

          updateTags = true;
          const newTags = data.tags.connect.map((tag) => {
            const selectedTag = tags.find((tagData) => tagData.id === tag.id);
            return selectedTag;
          });
          const tagsUniq = uniqBy([...x.tags, ...newTags], (v) => v.id);
          console.log("updatedTags ====> ", tagsUniq);

          return { ...x, tags: tagsUniq };
        }
        if (data.tags.disconnect.length > 0) {
          console.log("REMOVING TAGS");

          console.log("data.tags.disconnect ====> ", data.tags.disconnect);

          updateTags = true;
          const newTags = differenceWith(
            x.tags,
            data.tags.disconnect,
            (value, other) => value.id === other.id
          );

          console.log("updatedTags ====> ", newTags);

          return { ...x, tags: newTags };
        }
        return { ...x };
      }
      return { ...x };
    });
    if (selectedConversation.id === data.id) {
      let payload = null;
      if (data.tags.connect?.length > 0) {
        const newTags = data.tags.connect.map((tag) => {
          const selectedTag = tags.find((tagData) => tagData.id === tag.id);
          return selectedTag;
        });

        const tagsUniq = uniqBy(
          [...selectedConversation.tags, ...newTags],
          (v) => v.id
        );
        payload = {
          ...(payload ?? {}),
          data: newConversations,
          current: {
            ...selectedConversation,
            tags: tagsUniq,
          },
        };
      }
      if (data.tags.disconnect?.length > 0) {
        const newTags = differenceWith(
          selectedConversation.tags,
          data.tags.disconnect,
          (value, other) => value.id === other.id
        );
        payload = {
          ...(payload ?? {}),
          data: newConversations,
          current: {
            ...selectedConversation,
            tags: [...newTags],
          },
        };
      }

      if (payload !== null) {
        dispatch({ type: SET_CONVERSATION_AS_SELECTED, payload });
      }
    } else if (updateTags) {
      dispatch({ type: SET_CONVERSATIONS, payload: newConversations });
    }
  } catch (error) {
    console.error(error);
  }
};

export const clientTagAction =
  (clientId, tag, add) => async (dispatch, getState) => {
    try {
      if (add) {
        await connectTag(clientId, tag.id);
      } else {
        await disconnectTag(clientId, tag.id);
      }
      const conversations = [...getState().conversations.data];
      const selectedConversation = { ...getState().conversations.current };
      const newConversations = conversations.map((storeConversation) => {
        if (storeConversation.id === clientId) {
          const currentTags = storeConversation.tags || [];
          const newStoreConversation = {
            ...storeConversation,
            tags: add === true
              ? [...currentTags, tag]
              : currentTags.filter((x) => x.id !== tag.id),
          };
          selectedConversation.tags = [...newStoreConversation.tags];
          return newStoreConversation;
        }
        return storeConversation;
      });

      if (selectedConversation.id === clientId && !conversations.some((c) => c.id === clientId)) {
        const currentTags = selectedConversation.tags || [];
        selectedConversation.tags = add === true
          ? [...currentTags, tag]
          : currentTags.filter((x) => x.id !== tag.id);
      }

      const payload = {
        data: newConversations,
        current: { ...selectedConversation },
        isCurrentWindowOpen: getState().conversations.isCurrentWindowOpen,
      };
      dispatch({ type: SET_CONVERSATION_AS_SELECTED, payload });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const sendToCrmAction = (clientId) => async () => {
  try {
    const clientUpdated = await sendToCrm(clientId);
    return clientUpdated;
  } catch (error) {
    console.error(error);
    return { error: true, reason: UNEXPECTED_ERROR };
  }
};

export const getClientNameFromNotification = (data) => (dispatch, getState) => {
  try {
    const { id } = getState().conversations.current;
    if (id === data.id) {
      dispatch({
        type: UPDATE_USER_NAME,
        payload: {
          nameFromChannel: data.nameFromChannel,
          cleanedName: data.cleanedName,
        },
      });
    }
    const conversations = [...getState().conversations.data];
    let updateConversationList = false;
    const newConversations = conversations.map((c) => {
      if (c.id === data.id) {
        updateConversationList = true;
        return {
          ...c,
          nameFromChannel: data.nameFromChannel,
          cleanedName: data.cleanedName,
        };
      }
      return { ...c };
    });
    if (updateConversationList) {
      // Pusher `newUserName`: rewrites a row already in the list.
      dispatch(
        withTelemetrySource(
          { type: SET_CONVERSATIONS, payload: newConversations },
          TELEMETRY_SOURCES.PUSHER
        )
      );
    }
  } catch (error) {
    console.error(error);
  }
};

export const updateClientUsername =
  (newUsername, phone) => async (dispatch, getState) => {
    try {
      const { id, whatsappUsername } = getState().conversations.current;
      const searchToken = phone || whatsappUsername || '';
      const cleanedName = cleanString(newUsername + searchToken);
      const clientUpdated = await updateClientUsernameMutation(
        id,
        newUsername,
        cleanedName
      );

      const conversations = [...getState().conversations.data];
      const newConversations = conversations.map((c) => {
        if (c.id === id) {
          return { ...c, nameFromChannel: newUsername, cleanedName };
        }
        return { ...c };
      });
      dispatch({ type: SET_CONVERSATIONS, payload: newConversations });

      return clientUpdated;
    } catch (error) {
      console.error(error);
      return { error: true, reason: UNEXPECTED_ERROR };
    }
  };

export const updateClientPhone = (newPhone) => async (dispatch, getState) => {
  try {
    const current = getState().conversations.current;
    const { id, key, senderId, cleanedName, company, channel, phoneNumber } = current;

    // Se pregunta por la identidad con el helper, nunca por senderIdType crudo:
    // la etiqueta viene mal estampada en origen (backend#288) y leerla directo
    // aquí bloqueaba el guardado de ~13,000 contactos telefónicos a los que el
    // panel sí les dibuja el lápiz de editar — el lápiz aparecía y el guardado
    // fallaba con dos toasts, uno de ellos falso. Ver whatsappIdentity.js y #213.
    if (isUserIdIdentity(current)) {
      toast.error('No se puede editar el teléfono de contactos sin número disponible');
      return { error: true, reason: PHONE_NOT_EDITABLE };
    }

    const currentPhone = getPhoneOrNull(current) || senderId?.split("@")?.[0];

    if (newPhone.startsWith("52")) {
      newPhone = `${newPhone.substring(0, 2)}1${newPhone.substring(
        2,
        newPhone.length
      )}`;
    } else if (newPhone.startsWith("54")) {
      newPhone = `${newPhone.substring(0, 2)}9${newPhone.substring(
        2,
        newPhone.length
      )}`;
    }

    const existedPhones = await checkExistedPhone(
      company.id,
      channel.id,
      newPhone,
      id
    );

    console.log("existedPhones READYY=======>", existedPhones);

    if (existedPhones.length === 0) {
      const updatedkey = key.replace(currentPhone, newPhone);
      const updatedSenderId = senderId.replace(currentPhone, newPhone);
      const updatedCleanedName = cleanedName.replace(currentPhone, newPhone);

      const clientUpdated = await updateClientPhoneMutation(
        id,
        updatedCleanedName,
        updatedkey,
        updatedSenderId,
        newPhone
      );

      dispatch({
        type: UPDATE_USER_PHONE,
        payload: {
          cleanedName: updatedCleanedName,
          key: updatedkey,
          senderId: updatedSenderId,
          phoneNumber: newPhone,
          senderIdType: 'phoneNumber',
        },
      });

      const conversations = [...getState().conversations.data];
      const newConversations = conversations.map((c) => {
        if (c.id === id) {
          return {
            ...c,
            cleanedName: updatedCleanedName,
            key: updatedkey,
            senderId: updatedSenderId,
            phoneNumber: newPhone,
            senderIdType: 'phoneNumber',
          };
        }
        return { ...c };
      });
      dispatch({ type: SET_CONVERSATIONS, payload: newConversations });

      return clientUpdated;
    }
    return { error: true, reason: DUPLICATED_PHONE };
  } catch (error) {
    // Forma DISCRIMINADA, no el Error crudo: un Error de JS no tiene `.error`, así
    // que devolverlo hacía que el consumidor leyera `undefined` → falsy → toast de
    // éxito sobre una operación que había reventado. Ver #216.
    console.error("error at updateClientPhone ==> ", error);
    return { error: true, reason: UNEXPECTED_ERROR };
  }
};

export const readBroadcastChats = (isNewBroadcastChat) => (dispatch) => {
  // const payload = { isNewBroadcastChat };
  dispatch({
    type: UPDATE_UNREAD_BROADCAST_CHAT,
    payload: { isNewBroadcastChat },
  });
};

export const setBroadcastChat = (count) => async (dispatch) => {
  const payload = { unreadBroadcastChat: count };
  dispatch({ type: GET_BROADCAST_CONVERSATIONS, payload });
};

export const getBroadCastConversationsAction =
  () => async (dispatch, getState) => {
    try {
      const companyId = getState().user.data.company.id;
      const response = await getBroadCastConversationsQuery(companyId);
      console.log(response.clients.length);
      dispatch(setBroadcastChat(response.clients.length));
      return;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

export const updateValuableByUserAction =
  (value) => async (dispatch, getState) => {
    try {
      const { id } = getState().conversations.current;
      const clientUpdated = await updateValuableByUserMutation(id, value);
      const conversations = [...getState().conversations.data];
      const index = conversations.findIndex((x) => x.id === id);
      conversations[index] = {
        ...conversations[index],
        isConversationValuable: value,
      };
      dispatch({
        type: UPDATE_VALUABLE_CLASSIFICATION,
        payload: { value, data: conversations },
      });
      return clientUpdated;
    } catch (error) {
      console.error(error);
      return { error: true, reason: UNEXPECTED_ERROR };
    }
  };

export const clearConversationsAction = () => ({
  type: CLEAR_CONVERSATIONS,
  payload: null,
});

export const clearDataAction = () => ({
  type: CLEAR_DATA,
  payload: null,
});

export const updateHasFollowUpMessage =
  (hasFollowUpMessage) => async (dispatch, getState) => {
    const { id } = getState().conversations.current;
    const conversations = [...getState().conversations.data];
    const index = conversations.findIndex((x) => x.id === id);
    conversations[index] = {
      ...conversations[index],
      hasFollowUpMessage: hasFollowUpMessage,
    };

    dispatch({
      type: UPDATE_HASFOLLOWUPMESSAGE,
      payload: { hasFollowUpMessage, data: conversations },
    });
  };

export const updateBoardHasFollowUpMessage =
  (hasFollowUpMessage, conversation) => async (dispatch, getState) => {
    const { id } = conversation;
    const conversations = [...getState().conversations.data];
    const index = conversations.findIndex((x) => x.id === id);
    conversations[index] = {
      ...conversations[index],
      hasFollowUpMessage: hasFollowUpMessage,
    };

    dispatch({
      type: UPDATE_HASFOLLOWUPMESSAGE,
      payload: { hasFollowUpMessage, data: conversations },
    });
  };

export const conversationDeletRequest = (clientId) => async () => {
  const data = { clientId };
  const deleteConversationQ = await deleteConversation(data);
  return !!deleteConversationQ;
};
export const conversationArchived = (clientId, isArchived) => async (_) => {
  const archivedConversationQ = await setIsArchived(clientId, isArchived);
  return !!archivedConversationQ;
};

export const deleteConversationAction =
  (data) => async (dispatch, getState) => {
    const conversations = [...getState().conversations.data];
    const current = { ...getState().conversations.current };
    const newConversations = conversations.filter(
      (x) => x.id !== data?.client?.id
    );
    if (data?.client?.id === current?.id) {
      dispatch({ type: CLEAR_SELECTED_CONVERSATION });
    }
    dispatch({ type: SET_CONVERSATIONS, payload: newConversations });
  };

export const archivedConversationAction =
  (data) => async (dispatch, getState) => {
    const archived = getState().filters.type;

    if (archived === "Archived") {
      if (data.client.isArchived) {
        if (!data.client.newMessage) {
          const archivedConversations = [...getState().conversations.data];
          archivedConversations.unshift(data.client);
          dispatch({
            type: SET_CONVERSATIONS,
            payload: archivedConversations,
          });
        } else {
          data.type = "newMessage";
          dispatch(updateConverations(data));
        }
      } else {
        dispatch(deleteConversationAction(data));
      }
    } else if (data.client.isArchived) {
      dispatch(deleteConversationAction(data));
    } else {
      data.type = "newMessage";
      dispatch(updateConverations(data));
    }
  };

export const archivedBroadConversationAction =
  (data) => async (dispatch, getState) => {
    const archived = getState().filters.type;
    const { unreadBroadcastChat } = getState().conversations;

    if (data.client.platform.name === "SMS") {
      dispatch(updateConverations(data));
    } else {
      if (archived === "ArchivedBroadcast") {

        if (data.client.isBroadcastMessage) {
          if (!data.client.newMessage) {
            const archivedConversations = [...getState().conversations.data];
            archivedConversations.unshift(data.client);
            dispatch({
              type: SET_CONVERSATIONS,
              payload: archivedConversations,
            });
          } else {
            data.type = "newMessage";
            dispatch(updateConverations(data));
          }
        } else {
          dispatch(deleteConversationAction(data));
        }
        dispatch({
          type: GET_BROADCAST_CONVERSATIONS,
          payload: { unreadBroadcastChat: 0 },
        });
      } else if (data.client.isBroadcastMessage) {
        dispatch(deleteConversationAction(data));
        // const broadcast = await getBroadCastConversationsQuery(
        //   data.client.company.id
        // );
        dispatch({
          type: GET_BROADCAST_CONVERSATIONS,
          payload: { unreadBroadcastChat: unreadBroadcastChat + 1 },
        });
      } else {
        dispatch({
          type: GET_BROADCAST_CONVERSATIONS,
          payload: { unreadBroadcastChat: unreadBroadcastChat - 1 },
        });
      }
    }
  };

export const resetConversation = () => (dispatch) => {
  dispatch({ type: RESET_CONVERSATION_CURRENT });
};

export const setLastSurveyInfoDate = () => (dispatch) => {
  dispatch({ type: SET_CURRENT_LAST_SURVEY_INFO });
};

export const setConversationClosed = (data) => (dispatch, getState) => {
  const conversationsData = [...getState().conversations.data];
  const currentConversation = { ...getState().conversations.current };
  const conversationIndex = conversationsData.findIndex(
    (x) => data.client.id === x.id
  );

  const newConversationsData = [...conversationsData];

  if (conversationIndex > 0) {
    newConversationsData[conversationIndex].isClosed = data.client.isClosed;
    dispatch({ type: SET_CONVERSATIONS, payload: newConversationsData });
  }

  if (currentConversation && currentConversation?.id === data.client.id) {
    const newCurrentConversation = {
      ...currentConversation,
      isClosed: data.client.isClosed,
    };
    dispatch({
      type: SET_CURRENT_CONVERSATION,
      payload: newCurrentConversation,
    });
  }
};

const getClientInfoByClients = async (clients) => {
  let clientInfoByClient = [];
  try {
    const getClientInformation = async (clientId) => {
      const clientInfo = [];
      const data = await getClientInformationUtil(clientId);
      // console.log("data", data);
      // console.log("clientInfoes", data.clientInfoes);
      // // const info = data?.clientInfo;
      for await (const info of data?.clientInfoes) {
        // console.log("info", info);

        clientInfo.push({
          name: info?.fieldName?.name,
          value: info?.value,
        });
      }
      // console.log("clientInfo", clientInfo);
      return clientInfo;
    };

    const clientInfoes = await Promise.all(
      clients.map(async (client) => {
        const info = await getClientInformation(client?.id);
        return { id: client?.id, info };
      })
    );

    // console.log("clientInfoesN", clientInfoes);

    clientInfoByClient = groupBy(clientInfoes, (y) => y.id);
    console.log("clientInfo", clientInfoByClient);
    return clientInfoByClient;
  } catch (error) {
    console.log(error);
  }
};

// What the Contacts requests (list, as-you-type search, export) may ask for.
// The backend reads a missing or empty `channelsIds` as "every channel of
// every space the user belongs to", so a request only goes out without them
// when that cannot bring anything from another space:
//  - a space is selected: `channels` is its channel list;
//  - no space is selected and the spaces read settled with an empty list: the
//    user has no space at all, `selected` stays `{}` for good, and the request
//    goes out as it always did (the same test as `hasNoSpaces` in ChatView);
//  - anything else is not settled (the spaces were not read yet, or were left
//    without a list or a selection): nothing is asked for, and the Contacts
//    view loads its list when a space gets selected.
const getContactsSpaceScope = (spaces) => {
  const channels = spaces?.selected?.channels;
  if (Array.isArray(channels)) return { settled: true, channels };
  const userHasNoSpaces =
    spaces?.success === true &&
    !spaces.fetching &&
    Array.isArray(spaces.data) &&
    spaces.data.length === 0;
  return { settled: userHasNoSpaces, channels: undefined };
};

export const searchClientReportAction =
  (
    filterContactsBy,
    initialDate,
    endDate,
    filters,
    skip,
    rol,
    agentId,
    keyword,
    tagsIds
  ) =>
  async (_, getState) => {
    try {
      const companyId = getState().user.data.company.id;
      const companyName = getState().user.data.company.name;
      //const rol = getState().user.data.rol.name;
      const userId = getState().user.data.id;
      const isAdminOfIds = getState().user.data.isAdminOfIds;

      // The Contacts list is bounded by the selected space: every channel of
      // the space, so no contact that belongs to it is left out. The ids were
      // computed here and never sent, and without `channelsIds` the backend
      // answers with every channel of every space the user belongs to.
      // Without a selected space see `getContactsSpaceScope`.
      const spaceScope = getContactsSpaceScope(getState().spaces);
      if (!spaceScope.settled) return [];
      const channelIds = spaceScope.channels
        ? spaceScope.channels.map((x) => x?.id).filter((id) => id)
        : undefined;
      console.log("channelIds", channelIds);
      // The backend reads an empty list like a missing one, so a space without
      // channels is not asked for at all: it has no contact to show.
      if (channelIds && channelIds.length === 0) return [];

      let agentIdIn = [];
      if (userId && isAdminOfIds && isAdminOfIds.length > 0) {
        agentIdIn = isAdminOfIds;
      }

      const response = await API.post("/conversations_by_filter", {
        query: {
          filterContactsBy,
          keyword: cleanString(keyword) === "" ? null : keyword,
          companyId,
          createdAtFrom: initialDate,
          createdAtTo: endDate,
          skip: skip ?? 0,
          agentId:
            rol === CONSTANTS.ADMIN || rol === CONSTANTS.SUPERADMIN
              ? agentId === "Todos"
                ? null
                : agentId
              : userId,
          custom: true,
          first: 50,
          tagsIds,
          ...(agentIdIn.length > 0 && { agentIdIn }),
          ...(channelIds && { channelsIds: channelIds }),
        },
      });

      const clients = response?.data ?? [];

      // const clients = await getClientReport(
      //   companyId,
      //   companyName,
      //   initialDate,
      //   endDate,
      //   channelIds,
      //   filters,
      //   skip,
      //   rol,
      //   agentId
      // );
      console.log("clients", clients);
      let clientInfoByClient = [];
      if (companyName !== CONSTANTS.VINTE_COMPANY_NAME) {
        clientInfoByClient = await getClientInfoByClients(clients);
      }

      return clients.map((client) => {
        // console.log("client: ", client);
        const c = {
          id: client?.id,
          nameFromChannel: client?.nameFromChannel.split(" ")[0],
          lastName: client?.nameFromChannel.split(" ").slice(1).join(" "),
          agent: client?.agent?.name || "",
          tags:
            client?.tags?.length > 0
              ? client?.tags?.reduce(
                  (pV, cV, cI) => pV + (cI > 0 ? ", " : "") + cV.name,
                  ""
                )
              : "",
          plataform: client?.channel?.platform?.name || "",
          channel: client?.channel?.name || "",
          number: client?.senderId?.replace("@c.us", "") || "",
          createdAt: moment(client?.createdAt).format("DD-MM-YYYY HH:mm") || "",
          lastUpdate:
            moment(client?.lastUpdate)?.format("DD-MM-YYYY HH:mm") || "",
          clientInfo: clientInfoByClient[client?.id]
            ? clientInfoByClient[client?.id][0].info
            : [],
        };
        if (client.name) {
          c.name = client.name;
        }
        if (client.correoElectronico) {
          c.email = client.correoElectronico;
        }
        if (client.plaza) {
          c.plaza = client.plaza;
        }
        if (client.number) {
          c.number =
            client.number?.replace("@c.us", "") ||
            client?.senderId?.replace("@c.us", "") ||
            "";
        }
        return c;
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  };
export const exportClientReportAction =
  (filterContactsBy, initialDate, endDate, keyword, tagsIDS, agentID) =>
  async (_, getState) => {
    try {
      const companyId = getState().user.data.company.id;
      const companyName = getState().user.data.company.name;
      const userId = getState().user.data.id;
      const isAdminOfIds = getState().user.data.isAdminOfIds;
      // Without a selected space see `getContactsSpaceScope`.
      const spaceScope = getContactsSpaceScope(getState().spaces);
      if (!spaceScope.settled) return [];
      const channelIds = spaceScope.channels
        ?.filter((x) => x?.isActive === true)
        .map((x) => x.id);
      console.log("channelIds", channelIds);
      console.log("KEYWORD ====>", keyword);
      // Same rule as the Contacts list: the backend reads an empty channel
      // list like a missing one and answers with every channel of every space
      // the user belongs to. A selected space with no active channel has
      // nothing this export may contain, so it is not asked for at all.
      if (Array.isArray(channelIds) && channelIds.length === 0) return [];
      const clients = await getClientReportToExport(
        filterContactsBy,
        companyId,
        companyName,
        keyword ? null : initialDate,
        keyword ? null : endDate,
        channelIds,
        keyword ? cleanString(keyword) : null,
        tagsIDS,
        agentID,
        userId,
        isAdminOfIds
      );
      console.log("clients", clients);
      let clientInfoByClient = [];
      if (companyName !== CONSTANTS.VINTE_COMPANY_NAME) {
        // clientInfoByClient = await getClientInfoByClients(clients);
        clientInfoByClient = [];
      }

      return clients.map((client) => {
        // console.log("client: ", client);
        const c = {
          id: client?.id,
          nameFromChannel: client?.nameFromChannel.split(" ")[0],
          lastName: client?.nameFromChannel.split(" ").slice(1).join(" "),
          agent: client?.agent?.name || "",
          tags:
            client?.tags?.length > 0
              ? client?.tags?.reduce(
                  (pV, cV, cI) => pV + (cI > 0 ? ", " : "") + cV.name,
                  ""
                )
              : "",
          plataform: client?.channel?.platform?.name || "",
          channel: client?.channel?.name || "",
          number: client?.senderId?.replace("@c.us", "") || "",
          createdAt: moment(client?.createdAt).format("DD-MM-YYYY HH:mm") || "",
          lastUpdate:
            moment(client?.lastUpdate)?.format("DD-MM-YYYY HH:mm") || "",
          // clientInfo: clientInfoByClient[client?.id]
          //   ? clientInfoByClient[client?.id][0].info
          //   : [],
        };
        if (client.name) {
          c.name = client.name;
        }
        if (client.correoElectronico) {
          c.email = client.correoElectronico;
        }
        if (client.plaza) {
          c.plaza = client.plaza;
        }
        if (client.number) {
          c.number =
            client.number?.replace("@c.us", "") ||
            client?.senderId?.replace("@c.us", "") ||
            "";
        }
        return c;
      });
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

export const searchClientByKeywordAction =
  (keyword, selectedAgentId, skip) => async (_, getState) => {
    try {
      console.log("nOentrakeyword", keyword);
      console.log("keyword", keyword);
      const cleanedName = keyword
        ?.trim()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s/g, "")
        .toLowerCase();

      const companyId = getState().user.data.company.id;
      const companyName = getState().user.data.company.name;
      const userId = getState().user.data.id;
      const rolName = getState().user.data.rol.name;
      const isAdminOfIds = getState().user.data.isAdminOfIds;
      const isVinte = companyName === CONSTANTS.VINTE_COMPANY_NAME;
      const isAgent =
        rolName !== CONSTANTS.SUPERADMIN &&
        rolName !== CONSTANTS.ADMIN &&
        rolName !== CONSTANTS.GROUPADMIN;

      const userSpaces = getState().user.data.spaces;
      const agents = getState().agents.data;

      // Same rule as the Contacts list and the export: without a selected
      // space see `getContactsSpaceScope`.
      const spaceScope = getContactsSpaceScope(getState().spaces);
      if (!spaceScope.settled) return [];
      const hasSpaceSelection = Array.isArray(spaceScope.channels);
      const channels = hasSpaceSelection ? spaceScope.channels : [];

      let selectedPagesIds = [];
      console.log("keyword", cleanedName);

      if (
        userSpaces.length === 1 &&
        userSpaces[0]?.name === CONSTANTS.VINTE_DEFAULT_NAME
      ) {
        selectedPagesIds = [
          CONSTANTS.VINTE_DEFAULT_WHATSAPP,
          CONSTANTS.VINTE_DEFAULT_FACEBOOK,
        ];
      } else {
        selectedPagesIds = channels
          .filter(
            (c) => c?.platform?.name !== CONSTANTS.SMS_PLATFORM && c?.isActive
          )
          .map((e) => e.id);
      }
      const selectedPagesIdsCleaned = selectedPagesIds.filter((e) => e);
      const selectedSpacesIds = selectedPagesIdsCleaned;
      let agentIdIn = [];
      if (agents?.length > 0) {
        const isUnassignedAgentSelected = agents.some(
          (x) => x.id === CONSTANTS.UNASSIGNED_AGENT_ID
        );
        let agentsId = agents
          .filter((x) => x.id !== CONSTANTS.UNASSIGNED_AGENT_ID)
          .map((x) => x.id);
        if (isUnassignedAgentSelected) agentsId = [...agentsId, ""];
        agentIdIn = agentsId;
      }

      // let channelsNames;
      // if (channels?.length > 0) {
      //   if (!isVinte || (isVinte && !isAgent)) {
      //     const getChannelsNames = map(channels, (channel) => channel.name);
      //     channelsNames = [...getChannelsNames];
      //   }
      // }
      //console.log("getConversations channelsNames => ", channelsNames);

      let channelsIds;
      if (selectedSpacesIds?.length > 0) {
        if (!isVinte) channelsIds = selectedSpacesIds;
        if (isVinte && !isAgent) channelsIds = selectedSpacesIds;
        if (!isVinte || (isVinte && !isAgent)) channelsIds = selectedSpacesIds;
      }
      console.log("getConversations channelsIds => ", channelsIds);
      console.log("keyword channelsIds => ", keyword);
      // Same rule as the chat search (`getConversations`): the selected space
      // has no channel this search may read (every channel is SMS or
      // disconnected). Without `channelsIds` the backend falls back to every
      // channel of every space the user belongs to, so that search is not
      // sent. A Vinte agent is left out: their search was never bounded by
      // channel, only by their own id.
      const spaceHasNoSearchableChannel =
        hasSpaceSelection &&
        selectedSpacesIds.length === 0 &&
        (!isVinte || !isAgent);
      if (spaceHasNoSearchableChannel) return [];
      const graphQuerySearchFilter = {
        companyId,
        keyword: cleanedName,
        channelsIds,
        //channelsNames,
        ...(isAgent ? { agentId: userId } : {}),
        ...(selectedAgentId && {
          agentId: selectedAgentId === "Todos" ? null : selectedAgentId,
        }),
        ...(rolName === CONSTANTS.GROUPADMIN ? { groupAdminId: userId } : {}),
      };
      console.log("graphQuerySearchFilter", graphQuerySearchFilter);
      // const data = await client.query({
      //   query: ApolloConversations.GET_CONVERSATIONS_BY_SEARCHING_KEYWORD(
      //     graphQuerySearchFilter
      //   ),
      //   variables: graphQuerySearchFilter,
      // });

      if (userId && isAdminOfIds && isAdminOfIds.length > 0) {
        graphQuerySearchFilter.agentIdIn = isAdminOfIds;
      }

      const response = await API.post("/conversations_by_filter", {
        query: {
          ...graphQuerySearchFilter,
          skip: skip ?? 0,
          custom: true,
          first: 100,
        },
      });

      console.log("Search Contac data => ", response?.data);
      return response?.data ?? [];
    } catch (error) {
      console.log("error", error);
      throw error;
    }
  };

export const getConversationsPanel =
  ({
    initialDate,
    finalDate,
    noAnswered,
    archived,
    unread,
    whatsappChannel,
    messengerChannel,
    agents,
    broadcast,
    tags,
    isConversationValuable,
  }) =>
  async (dispatch) => {
    console.log("answered", noAnswered);
    console.log("isArchived", archived);
    console.log("unread", unread);
    console.log("whatsappChannel", whatsappChannel);
    console.log("messengerChannel", messengerChannel);
    console.log("agents", agents);
    console.log("broadcast", broadcast);
    console.log("tags", tags);
    console.log("isConversationValuable", isConversationValuable);

    const filters = {
      noAnswered,
      isArchived: archived,
      whatsappChannel,
      messengerChannel,
      agents,
      isArchivedBroadcast: broadcast,
      tags,
      isConversationValuable,
    };

    const fetchedConversations = await dispatch(
      searchClientReportAction(initialDate, finalDate, filters)
    );

    console.log("fetchedConversations", fetchedConversations);
    return fetchedConversations;
  };

export const handleGroupAdmin = (data) => async (dispatch, getState) => {
  console.log("handleGroupAdmin +++++++++++++++++++");

  const userId = getState().user.data?.id;
  const rolName = getState().user.data.rol.name;

  if (rolName === CONSTANTS.GROUPADMIN) {
    console.log("is group admin rol");
    const conversations = [...getState().conversations.data];

    if (data.userId !== userId) {
      console.log("different IDs");
      const current = { ...getState().conversations.current };
      const newConversations = conversations.filter(
        (x) => x.id !== data?.client?.id
      );
      if (data?.client?.id === current?.id) {
        dispatch({ type: CLEAR_SELECTED_CONVERSATION });
      }
      // Pusher `groupAdmin`: drops a row; the insert branch below goes
      // through updateConverations, which labels itself.
      dispatch(
        withTelemetrySource(
          { type: SET_CONVERSATIONS, payload: newConversations },
          TELEMETRY_SOURCES.PUSHER
        )
      );
    } else {
      console.log("same IDs");
      const index = conversations.findIndex(
        (conversation) => conversation?.id === data?.client?.id
      );
      if (index === -1) {
        console.log("there is no conversation", data?.client?.id);

        dispatch(updateConverations(data));
      }
    }
  }
};

/**
 * Updates `hiddenFromHumans` / `viewableForHumans` of one conversation in the
 * list and in the open chat (R4). Only the keys present in `flags` change.
 */
export const setConversationVisibilityFlagsAction = (clientId, flags) => ({
  type: UPDATE_CONVERSATION_VISIBILITY,
  payload: { clientId, flags },
});

/** Same page size ChatView and the reconnect recovery use. */
const VISIBILITY_REFETCH_PAGE_SIZE = 20;

/**
 * Which open chat survives a bulk reload. `getConversationsAction` re-selects
 * the chat it is given from memory, so for a non-exempt user it is asked to
 * the server first: if the chat became hidden (`null`) or the check fails,
 * it is closed instead of kept on screen (R5).
 */
const openChatToKeep = async (state, exempt) => {
  const openId = state.conversations.current?.id;
  if (!openId) return null;
  if (exempt) return openId;
  try {
    const stillVisible = await getConversationID(openId);
    return stillVisible?.id ? openId : null;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: "conversationsDuck", op: "conversationVisibilityBulk" },
    });
    return null;
  }
};

/**
 * Pusher `conversationVisibilityChanged` (R6):
 * - `{ bulk: true }`: many conversations changed at once → reload the inbox
 *   with the current filters, keeping the open chat when it is still visible.
 * - exempt users: they keep seeing everything; only the flag changes.
 * - everyone else, `hidden: false`: the conversation is fetched by id and goes
 *   through `updateConverations`, so it is inserted only if it passes the same
 *   checks as any `newClient` event. (`hidden: true` never reaches them: the
 *   backend sends `deleteConversation` instead.)
 */
export const handleConversationVisibilityChanged =
  (data) => async (dispatch, getState) => {
    if (!data || typeof data !== "object") return;
    const exempt = isConversationVisibilityExempt(getState().user.data);
    if (data.bulk === true) {
      const keepOpenId = await openChatToKeep(getState(), exempt);
      await dispatch(
        getConversationsAction(true, VISIBILITY_REFETCH_PAGE_SIZE, keepOpenId)
      );
      return;
    }
    const clientId = data.client?.id;
    if (!clientId || typeof data.hidden !== "boolean") return;

    if (exempt) {
      dispatch(
        setConversationVisibilityFlagsAction(clientId, {
          hiddenFromHumans: data.hidden,
        })
      );
      return;
    }
    if (data.hidden) return;
    if (getState().conversations.data.some((c) => c.id === clientId)) return;

    try {
      const payload = await getConversationForRealtime(clientId);
      if (payload) await dispatch(updateConverations(payload));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "conversationsDuck", op: "conversationVisibilityChanged" },
      });
    }
  };
